import { Fragment, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { isNullish, isPromiseLike } from '@sas/nova-core/typeguards';
import { TableActions } from '@sas/react-svg';
import cx from 'classnames';
import { darken, lighten } from 'polished';
import { setAlpha } from '../../../design-tokens';
import { useReactDndConstructs } from '../../../dnd/ReactDndContext';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { useMounted } from '../../../hooks/useMounted';
import { useParentSize } from '../../../hooks/useParentSize';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { useRefArray } from '../../../hooks/useRefArray';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { getScrollbarSize } from '../../../util/dom/getScrollbarSize';
import { getAllFocusable } from '../../../util/focus';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { ContextMenu } from '../../ContextMenu';
import { useDensity } from '../../Density';
import { useDirectSpeech } from '../../DirectSpeech/DirectSpeechProvider';
import { Icon } from '../../Icon';
import { GlobalSettingsKeys, useGetGlobalSetting } from '../../Settings';
import { TableRegions } from '../TableRegions';
import { TableState } from '../TableState';
import { TableZeroState } from '../TableZeroState';
import { DraggableRow } from './DraggableRow';
import { getTableOverrideTokens } from './getTableOverrideTokens';
import { Defaults, defaultSizes, isCellControl, ManageColumnsDialog, TableCell, VariableSizeGrid } from './internal';
import { useDefaultAction } from './useDefaultAction';
import { useRowDragAndDrop } from './useRowDragAndDrop';
import { useTableNarration } from './useTableNarration';
import { useTableOverrideTheme } from './useTableOverrideTheme';
import bundle from '../NovaComponents-Table-gui-icu.properties';
import helpCn from '../../HelpPopup/__internal__/BaseHelpPopup.css.json';
import zeroStateCn from '../../ZeroState/ZeroState.css.json';
import cn from '../Table.css.json';
import '../Table.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const getSelected = (itemData, rowIndex) => {
  const {
    selectionMode,
    singleSelectionOverrides,
    getRowIdentifier
  } = itemData;
  if (itemData.preference?.selectAll === true) return false;
  const selectedRows = itemData.preference?.selectedRows;
  const singleSelection = selectionMode === 'single' || selectionMode === 'singleRadio';
  const allowDeselection = singleSelectionOverrides?.allowDeselection;
  return (!singleSelection || selectionMode === 'single' && allowDeselection !== false || selectionMode === 'singleRadio' && allowDeselection) && selectedRows?.has(getRowIdentifier(rowIndex)) ? false : true;
};
export const hasSelectionColumn = (selectionMode, columnCount, rowCount, selectionColumnHidden) => {
  if (selectionColumnHidden === true) return false;
  const shouldShowZeroState = columnCount === 0 || rowCount === 0;
  return !shouldShowZeroState && (shouldShowCheckbox(selectionMode) || selectionMode === 'singleRadio');
};
export const shouldShowCheckbox = selectionMode => {
  return selectionMode === undefined || selectionMode === 'multiple' || selectionMode === 'toggle';
};
export const getSortAction = ({
  column,
  direction,
  ctrlKey,
  isPrimarySortAction
}) => {
  const columnIsSorted = ['ascending', 'descending'].includes(column.sortDirection || '');
  const columnIsPrimarySort = column.sortLevel === 1;
  const columnIsSecondarySort = column.sortLevel && column.sortLevel > 1;
  if (columnIsSorted && direction === 'data') return 'remove';
  if (columnIsSorted && ctrlKey || columnIsSorted && (isPrimarySortAction && columnIsPrimarySort || !isPrimarySortAction && columnIsSecondarySort)) return 'reverse';
  return isPrimarySortAction ? 'set' : 'add';
};

/**
 * Returns the appropriate value from the options based on the supplied density setting.
 * The default will be the medium value if the density is unspecified.
 * @param values low, medium (default), and high values
 * @param density the current density setting
 * @returns the appropriate value for the given density
 */
function getDensityValue(values, density) {
  const [lowValue, medValue, highValue] = values;
  if (density === 'high') {
    return highValue;
  }
  if (density === 'low') {
    return lowValue;
  }
  return medValue;
}
const UnMemoizedBaseTable = ({
  readOnly,
  ...props
}) => {
  const isRTL = useIsRTL();
  const mounted = useMounted();
  const hasNoColumns = !props.columns || props.columns.length === 0;
  const interactiveActionRef = useRef(false);
  const setInteractiveAction = useCallback(value => {
    interactiveActionRef.current = typeof value === 'function' ? value(interactiveActionRef.current) : value;
  }, []);

  // The hasColumns state setter is invoked within onConfirmation handle of
  // ManageColumnDialog but it does not get set even by the time the onClose dialog
  // handle runs. It is probably batched or so and does not get a chance until the
  // exit animations are done. So rather than having a bigger timeout to run the
  // onClose handle callback, I feel it is more deterministic solution to use a Ref here.
  const hasColumns = useRef(true);
  const colKeys = useMemo(() => {
    return props.columns?.map(({
      key
    }) => key) || [];
  }, [props.columns]);
  const [currentDropIndicator, setCurrentDropIndicator] = useState();
  const initialOrderedColumnKeys = useMemo(() => {
    return props.columns?.filter(column => !column.hidden && !props.initialHiddenColumns?.includes(column.key)).map(({
      key
    }) => key) || [];
  }, [props.columns, props.initialHiddenColumns]);
  const getVisibleColumnKeys = useCallback(() => {
    const orderedColumnKeys = props.preference?.orderedColumnKeys;
    if (orderedColumnKeys?.length || !hasColumns.current) return orderedColumnKeys || [];
    if (initialOrderedColumnKeys?.length) return initialOrderedColumnKeys;
    return [];
  }, [initialOrderedColumnKeys, props.preference?.orderedColumnKeys]);
  const getRowIdentifier = useCallback(rowIndex => {
    const rowKey = props.getRowKey;
    return rowKey ? rowKey(rowIndex) : Defaults.getRowKey(rowIndex);
  }, [props.getRowKey]);
  const isRowDisabled = key => {
    if (props.isRowDisabled) return props.isRowDisabled(key);
    return props.disabledRows?.has(key) || false;
  };
  const isRowReadOnly = key => {
    if (props.isRowReadOnly) return props.isRowReadOnly(key);
    return props.readonlyRows?.has(key) || false;
  };
  const [setDimensions, dimensions] = useParentSize();
  const [clearAll, setClearAll] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [emptyDropZone, setEmptyDropZone] = useState(false);
  const rowRef = useRef();
  const [tableState, dispatch] = useReducer(TableState.reduce, TableState.init(props.preference?.selectedRows, props.preference?.selectAll, props.initialFocus, props.preference?.frozenColumnCount ?? 0, getVisibleColumnKeys()));
  const onFocusIndicator = props.onFocusIndicator;
  useEffect(() => {
    if (!mounted.current) return;
    onFocusIndicator?.(tableState.focusIndicator);
  }, [mounted, tableState.focusIndicator, onFocusIndicator]);
  const [focusVisible, setFocusVisible] = useState(props.initialFocusVisible);
  const selectionMode = !props.disabled ? props.selectionMode : 'none';
  const localize = useTextLocalizer();
  const [previousFocusIndicator, setPreviousFocusIndicator] = useState();
  const [contextMenuItems, setContextMenuItems] = useState();
  const [isContextMenuOpen, setContextMenuOpen] = useState(false);
  const [isManageColumnsDialogOpen, _setIsManageColumnsDialogOpen] = useState(false);
  const setIsManageColumnsDialogOpen = isOpen => {
    if (!props.disabled) {
      _setIsManageColumnsDialogOpen(isOpen);
    }
  };
  const [initialColumnWidth, setInitialColumnWidth] = useState();
  const [visibleRowStartIndex, setVisibleRowStartIndex] = useState(0);
  const [visibleRowStopIndex, setVisibleRowStopIndex] = useState(0);
  const [draggingRow, setDraggingRow] = useState(-1);
  const [contextInfo, setContextInfo] = useState({
    position: {
      x: 0,
      y: 0
    }
  });
  const [keyboardResizeColumn, setKeyboardResizeColumn] = useState();
  const [editState, setEditState] = useState({
    isEditing: false
  });
  const sortRef = useRef();
  const setSortColumn = columnKey => {
    sortRef.current = columnKey;
  };
  const [allowKeyDownSelection, setAllowKeyDownSelection] = useState(true);
  const defaultTableRef = useRef(null);
  // the top-most element for our grid structure
  const topRef = props.tableRef ?? defaultTableRef;
  const {
    refs: rowRefs,
    createSetRef: createRowRef
  } = useRefArray();
  const defaultHeaderRef = useRef(null);
  const headerRef = props.headerRef ?? defaultHeaderRef;
  // the react-window grid control itself
  const refGrid = props.internalRef ?? useRef(null);
  const bumperRef = useRef(null);
  const zeroStateRef = useRef(null);
  const focusIndicatorWidth = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_THICKNESS);

  // determine default row height based on density
  const {
    density
  } = useDensity();
  const densityDefaultHeight = getDensityValue([defaultSizes.lowDenistyRowHeight, defaultSizes.rowHeight, defaultSizes.highDensityRowHeight], density);
  const propRowHeight = props.rowHeight;
  const getRowHeight = useCallback(rowIndex => propRowHeight ? propRowHeight(rowIndex) : densityDefaultHeight, [propRowHeight, densityDefaultHeight]);
  const dt = useDesignTokens();
  const tokens = {
    ...getTableDesignTokens(dt, {
      cellBordersVisible: props.cellBordersVisible,
      ignoreTableOverrideTheme: props.ignoreTableOverrideTheme,
      selectionBorderStyle: props.selectionBorderStyle,
      outerBorderVisible: props.outerBorderVisible,
      focusIndicatorWidth,
      emptyDropZone,
      readOnly,
      disabled: props.disabled,
      density
    }),
    ...props.theme?.(dt)
  };
  const dndPrefix = props.dndPrefix ?? 'table';
  const didMount = useRef(false);
  const [columns, displayedColumnKeys] = useMemo(() => {
    const columns = props.columns;
    const orderedColumnKeys = props.preference?.orderedColumnKeys;
    const selection = selectionMode ?? Defaults.selectionMode;
    // for multiple selection, we need a space holder column for the checkbox
    const orderedColumns = [];
    const numOfOrderedKeys = orderedColumnKeys?.length || -1;
    if (!didMount.current || numOfOrderedKeys === 0 && !clearAll) setClearAll(false);
    const ordered = getVisibleColumnKeys();
    ordered?.forEach(item => {
      const column = columns?.find(e => e.key === item);
      column !== undefined && orderedColumns.push(column);
    });
    refGrid.current?.resetAfterColumnIndex(0, false);
    // show the selection control if there is a selection column and
    // the table is not in a zero state
    const showSelectionControl = hasSelectionColumn(selection, orderedColumns.length, props.rowCount, props.hideSelectionControlColumn);
    const columnKeys = orderedColumns.map(c => c.key);
    if (!showSelectionControl || orderedColumns.length === 0) return [orderedColumns, columnKeys];
    return [[{
      label: '',
      key: '',
      width: defaultSizes.selectionControlColumnWidth,
      minimumWidth: defaultSizes.selectionControlColumnWidth
    }, ...orderedColumns], columnKeys];
  }, [props.columns, props.preference?.orderedColumnKeys, props.rowCount, props.hideSelectionControlColumn, selectionMode, clearAll, refGrid, getVisibleColumnKeys]);
  const hasColumnActivation = columns?.find(e => e.onActivate !== undefined);
  const shouldShowZeroState = columns.length === 0 || props.rowCount === 0;
  const onActivate = (props.onActivate || hasColumnActivation) && !shouldShowZeroState && !props.doubleClickOnEnter ? (rowIndex, columnIndex, columnKey, isClick) => {
    const rowKey = getRowIdentifier(rowIndex);
    const column = columns[columnIndex];
    const hasCellLevelActivation = column && column.containsInteractiveContent && column.onActivate;
    if (!props.disabled && !readOnly && rowIndex > -1 && !isRowDisabled?.(rowKey) && !isRowReadOnly?.(rowKey) && !editState.isEditing) {
      const activateRef = columnKey ? itemData.getActivateRef(columnKey, rowKey) : undefined;

      // if onActivate is set on the column and the column has containsInteractiveContent
      // call onActivate on the column
      // this cell-level activation
      // Unless this is a click event. Click events should not trigger cell-level activation.
      // Pressing Enter should still trigger cell-level activation (NOVACOMMONS-14133)
      if (hasCellLevelActivation && !isClick) {
        column?.onActivate?.(rowIndex, {
          activateRef
        });
      }
      // otherwise, call onActivate on the table
      // this is row-level based activation
      else {
        props.onActivate?.(rowIndex, columnIndex, columnKey, isClick);
      }
    }
  } : undefined;
  const onDoubleClick = props.onDoubleClick ? oDCProps => {
    const rowKey = getRowIdentifier(oDCProps.rowIndex);
    if (!props.disabled && !readOnly && oDCProps.rowIndex > -1 && !isRowDisabled?.(rowKey) && !isRowReadOnly?.(rowKey)) {
      props.onDoubleClick?.(oDCProps);
      // Add and remove pressed class for row for onDoubleClick
      dispatch(TableState.actions.createPressedCell({
        cell: tableState.activeCell,
        pressRow: true
      }));
      setTimeout(() => {
        if (mounted.current) dispatch(TableState.actions.createPressedCell({
          cell: undefined
        }));
      }, 100);
    }
  } : undefined;
  const ignoreFocusIn = useRef();
  const needToAnnounceTotalSelectionRange = useRef();
  const needToAnnounceAddedSelectionRange = useRef();
  const selectionEndIndex = useRef();
  const focusableZeroStateElements = useRef([]);
  const contextMenuInfoRef = useRef();
  useEffect(() => {
    if (!mounted.current || !interactiveActionRef.current) return;
    const selectAll = props.preference?.selectAll;
    const selectedRows = props.preference?.selectedRows;
    const selectionMetadata = props.preference?.selectionMetadata;
    const onSelect = props.onSelect;
    onSelect?.(selectedRows, selectAll, selectionMetadata?.key, selectionMetadata?.state);
    interactiveActionRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preference?.selectedRows, props.preference?.selectAll]);
  useEffect(() => {
    if (!mounted.current || !interactiveActionRef.current) return;
    const forzenColumnCount = props.preference?.frozenColumnCount;
    const onFrozenColumnCountChange = props.onFrozenColumnCountChange;
    onFrozenColumnCountChange?.(forzenColumnCount || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preference?.frozenColumnCount]);
  useEffect(() => {
    if (!mounted.current || !interactiveActionRef.current) return;
    const orderedColumnKeys = getVisibleColumnKeys();
    const onOrderedColumnsChange = props.onOrderedColumnsChange;
    onOrderedColumnsChange?.(orderedColumnKeys);
    interactiveActionRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preference?.orderedColumnKeys]);
  useEffect(() => {
    // Clear interactiveActionRef after each render.
    interactiveActionRef.current = false;
  }, []);
  useEffect(() => {
    if (!zeroStateRef.current) return;
    focusableZeroStateElements.current = getAllFocusable(zeroStateRef.current);
  }, [shouldShowZeroState, props.emptyDataProps, props.emptyColumnProps]);
  const focusFirstZeroStateElement = useCallback(() => {
    if (mounted.current && focusableZeroStateElements.current[0]) {
      ignoreFocusIn.current = true;
      dispatch(TableState.actions.createFocusIndicator({
        focusIndicator: 'unfocused'
      }));
      dispatch(TableState.actions.createFocus({
        focusedCell: undefined
      }));
      focusableZeroStateElements.current[0].focus();
      ignoreFocusIn.current = false;
    }
  }, [focusableZeroStateElements, mounted]);
  const focusLastZeroStateElement = useCallback(() => {
    if (mounted.current && focusableZeroStateElements.current[focusableZeroStateElements.current.length - 1]) {
      ignoreFocusIn.current = true;
      dispatch(TableState.actions.createFocusIndicator({
        focusIndicator: 'unfocused'
      }));
      dispatch(TableState.actions.createFocus({
        focusedCell: undefined
      }));
      focusableZeroStateElements.current[focusableZeroStateElements.current.length - 1].focus();
      ignoreFocusIn.current = false;
    }
  }, [focusableZeroStateElements, mounted]);
  useEffect(() => {
    if (mounted.current && !props.disableAutoFocus && focusVisible) {
      const isTableActive = document.activeElement && document.activeElement.closest(`.${cn.table}`) !== null;
      !isTableActive && topRef.current?.focus();
      dispatch(TableState.actions.createFocusIndicator({
        focusIndicator: 'visible'
      }));
    }
  }, [props.disableAutoFocus, focusVisible, mounted, topRef]);
  useEffect(() => {
    const selectedRows = props.preference?.selectedRows;
    const areRowsVisible = props.areRowsVisible;
    if (selectedRows) {
      const selectedRowsValidated = !areRowsVisible ? true : areRowsVisible(selectedRows);
      const setSelectAllRows = props.setSelectAllRows;
      if (selectedRowsValidated && props.hideSelectAll !== true && props.hideSelectionControlColumn !== true && props.columnHeadersVisible !== false && props.rowCount > 0 && props.selectionMode === 'multiple' && selectedRows.size >= props.rowCount) {
        setSelectAllRows?.({
          selectAll: true
        });
      }
    }
  }, [props.preference?.selectedRows, props.areRowsVisible, props.setSelectAllRows, props.rowCount, props.hideSelectAll, props.columnHeadersVisible, props.hideSelectionControlColumn, props.selectionMode, props.disabledRows]);
  useEffect(() => {
    if (!mounted.current) return;
    // since zero state overlay is no longer a region that can get fauxcus
    // we need to actually set focus on the zero state unless disableAutoFocus is true
    if (props.initialFocus?.region === TableRegions.ZeroStateOverlay && !props.disableAutoFocus) {
      focusFirstZeroStateElement();
    } else {
      // if focus within hidden cell due to colspan, adjust to
      // first cell of the colspan range to have a proper
      // column index
      const columnDisplayed = props.initialFocus?.region === TableRegions.Data ? refGrid.current?._getRowMetadata(props.initialFocus.rowIndex).getCell(props.initialFocus.colIndex)?.displayed : -1;
      const initialFocus = props.initialFocus && columnDisplayed > -1 ? {
        ...props.initialFocus,
        colIndex: columnDisplayed
      } : props.initialFocus;

      // if disableAutoFocus is true and the region is ZeroStateOverlay, the focusHandler
      // will send focus to the ZeroState when the table gets focus
      dispatch(TableState.actions.createFocus({
        focusedCell: initialFocus
      }));
    }
  }, [props.initialFocus, focusFirstZeroStateElement, props.disableAutoFocus, mounted, refGrid]);

  // pay attention to whether the active element was the zero state (1st focusable child)
  const isFocusedOnZeroState = document.activeElement === focusableZeroStateElements.current?.[0];
  const wasFocusedOnZeroState = usePreviousValue(isFocusedOnZeroState);
  // if the zero state is no longer visible but was in focus on the prior render, focus on the main table element
  // otherwise, browser will put focus on body element as the last focused element is removed from the DOM
  if (wasFocusedOnZeroState && !isFocusedOnZeroState && !shouldShowZeroState) {
    if (props.initialFocus?.region !== TableRegions.ZeroStateOverlay && !props.disableAutoFocus) {
      topRef.current?.focus();
    }
  }
  useEffect(() => {
    // if the row count changed, make sure the fauxcus row is within the visible rows
    if (tableState.initialFocus?.rowIndex !== undefined && tableState.initialFocus?.rowIndex >= props.rowCount) {
      if (props.rowCount === 0 && tableState.focusIndicator === 'visible') {
        // if there are no rows, send focus to the zero state
        focusFirstZeroStateElement();
      } else {
        updateFauxcus({
          region: tableState.initialFocus?.region,
          colIndex: tableState.initialFocus?.colIndex,
          rowIndex: props.rowCount - 1
        });
      }
    }

    // We are making an exception to the lint rule in this case
    // because it wants to see tableState.initialFocus as a dependency.
    // There is already a useEffect watching tableState.initialFocus
    // and we do not want to duplicate that effort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rowCount]);
  const updateFauxcus = useCallback(info => {
    if (!mounted.current) return;
    if (info.region === TableRegions.Data || info.region === TableRegions.RowHeader && selectionMode === 'multiple') {
      // if we're focusing on the data region, make sure the rowIndex and columnIndex
      // is within the range of rows and columns
      const minColIndex = hasSelectionColumn(selectionMode, columns.length, props.rowCount, props.hideSelectionControlColumn) ? 1 : 0;
      if (info.rowIndex >= props.rowCount) info.rowIndex = Math.max(props.rowCount - 1, 0);
      if (info.colIndex >= columns.length) info.colIndex = Math.max(columns.length - 1, minColIndex);
    }
    const focusChange = props.onFocusChange;
    focusChange?.(info);
    dispatch(TableState.actions.createFocus({
      focusedCell: info
    }));
    setFocusVisible(tableState.focusIndicator === 'visible');
  }, [mounted, props.onFocusChange, props.rowCount, props.hideSelectionControlColumn, tableState.focusIndicator, selectionMode, columns.length]);
  useEffect(() => {
    if (!mounted.current) return;
    const scrollPosition = props.preference?.scrollPosition;
    if (!scrollPosition) return;
    const lastFocusedCell = tableState.lastFocusedCell;
    const focusChanged = scrollPosition?.cell.rowIndex !== lastFocusedCell?.rowIndex || scrollPosition?.cell.colIndex !== lastFocusedCell?.colIndex;
    refGrid.current?.scrollToItem({
      align: scrollPosition?.align ?? 'start',
      rowIndex: scrollPosition?.cell.rowIndex,
      columnIndex: scrollPosition?.cell.colIndex
    });
    if (scrollPosition?.focusToCell) {
      if (!scrollPosition?.editOnFocus) {
        ignoreFocusIn.current = true;
        // focus the table's topRef unless we're supposed to go into edit mode on focus
        topRef.current?.focus();
        ignoreFocusIn.current = false;
      }
      dispatch(TableState.actions.createFocus({
        focusedCell: scrollPosition?.cell
      }));
      if (focusChanged) props.onFocusChange?.(scrollPosition?.cell);
    }
    if (scrollPosition?.focusIndicator) {
      dispatch(TableState.actions.createFocusIndicator({
        focusIndicator: scrollPosition?.focusIndicator
      }));
    }
  },
  // This effect does not need to be triggered for onFocusChange
  // and tableState.lastFocusedCell changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [props.preference?.scrollPosition, topRef, mounted, refGrid]);
  const [startMeasurementColumnIndex, setStartMeasurementColumnIndex] = useState();
  const [startMeasurementRowIndex, setStartMeasurementRowIndex] = useState();
  useEffect(() => {
    setStartMeasurementColumnIndex(props.preference?.clearMeasurementsStartingColumnIndex);
    setStartMeasurementRowIndex(props.preference?.clearMeasurementsStartingRowIndex);
  }, [props.clearMeasurementCache, props.preference?.clearMeasurementsStartingColumnIndex, props.preference?.clearMeasurementsStartingRowIndex]);
  const getViewPortWidth = useCallback(() => {
    return props.expandToFillWidth && dimensions ? dimensions.width : props.width ?? defaultSizes.tableWidth;
  }, [props.width, props.expandToFillWidth, dimensions]);
  const measuredColumns = useMemo(() => {
    let estimatedWidth = 0;
    let knownWidth = estimatedWidth;
    let unknownCount = 0;
    const viewPortWidth = getViewPortWidth();
    const measuredSizes = new Map();
    let lastColumnKey;
    // get the column start index for the range to render
    // and start measuring the columns from there
    const currentRefGrid = refGrid.current;
    const rangeToRender = currentRefGrid?._getHorizontalRangeRendered();
    const startIndex = rangeToRender ? rangeToRender[2] ?? 0 : 0;
    columns?.slice(startIndex).some(column => {
      let width = props.preference?.resizedColumns?.has(column.key) ? props.preference?.resizedColumns?.get(column.key) : column?.width;
      const minColWidth = column.minimumWidth ?? defaultSizes.minimumColumnWidth;
      if (width !== undefined) width = Math.max(width, minColWidth);
      const index = columns?.findIndex(col => col.key === column.key);
      const columnWidthFn = props.columnWidth;
      if (width === undefined && columnWidthFn) {
        width = Math.max(columnWidthFn(index, column.key), defaultSizes.minimumColumnWidth);
      }
      if (width === undefined) unknownCount++;else knownWidth += width;
      estimatedWidth += width || defaultSizes.columnWidth;
      lastColumnKey = column.key;
      measuredSizes.set(column.key, width);
      return estimatedWidth >= viewPortWidth;
    });
    if (estimatedWidth > viewPortWidth) return measuredSizes;
    const availSpace = viewPortWidth - knownWidth;
    const newUnknownWidth = unknownCount ? Math.max(availSpace / unknownCount, defaultSizes.columnWidth) : availSpace;
    let totalWidth = 0;
    measuredSizes.forEach((value, key) => {
      if (value === undefined) {
        measuredSizes.set(key, newUnknownWidth);
        totalWidth += newUnknownWidth;
      } else {
        totalWidth += value;
      }
    });
    if (props.autoFillLastColumn !== false && lastColumnKey && totalWidth < viewPortWidth) {
      const lastWidth = measuredSizes.get(lastColumnKey) || defaultSizes.columnWidth;
      measuredSizes.set(lastColumnKey, viewPortWidth - totalWidth + lastWidth);
    }
    return measuredSizes;
  }, [columns, props.preference?.resizedColumns, props.columnWidth, props.autoFillLastColumn, getViewPortWidth, refGrid]);
  const getColumnFillerWidth = () => {
    let totalMeasuredWidth = 0;
    if (!props.columns || props.columns.length === 0) return 0;
    measuredColumns?.forEach(value => {
      totalMeasuredWidth += value ? value : defaultSizes.columnWidth;
    });
    const tableWidth = props.expandToFillWidth && dimensions ? dimensions.width : props.width ?? defaultSizes.tableWidth;
    if (totalMeasuredWidth < tableWidth) return tableWidth - totalMeasuredWidth;
    return 0;
  };
  const defaultAction = useDefaultAction({
    onActivate,
    columns,
    rowCount: props.rowCount,
    selectionMode,
    initialFocus: tableState.initialFocus,
    isList: props.hasOwnProperty('itemColumn') || props.hasOwnProperty('renderItem'),
    hideSelectionControlColumn: props.hideSelectionControlColumn
  });

  // Determine from cell metadata or internal columns, the colspan of the
  // given row/col indices (default is 1).
  const colSpanValue = (rowIndex, columnIndex) => {
    const {
      colSpan,
      key: columnKey
    } = columns[columnIndex] ?? {};
    const currentRefGrid = refGrid.current;
    const metadataCell = currentRefGrid?._getRowMetadata(rowIndex).getCell(columnIndex);
    if (metadataCell && metadataCell.colSpan > 1) return metadataCell.colSpan;
    return typeof colSpan === 'function' ? colSpan({
      columnIndex,
      rowIndex,
      columnKey
    }) : colSpan ?? 1;
  };
  const isColumnHeaderVisible = props.columnHeadersVisible !== false && !hasNoColumns;
  const tableNarration = useTableNarration({
    columns,
    columnHeadersVisible: isColumnHeaderVisible,
    frozenColumnCount: props.preference?.frozenColumnCount ?? 0,
    rowCount: props.rowCount,
    columnSpan: colSpanValue,
    selectAll: props.preference?.selectAll,
    selectionMode: selectionMode ?? Defaults.selectionMode,
    selectedRows: props.preference?.selectedRows,
    renderCell: props.renderCell ?? Defaults.renderCell,
    getRowKey: getRowIdentifier,
    infoProvider: props.accessibilityInfo,
    disabled: props.disabled,
    isRowDisabled,
    isRowReadOnly,
    getSelectionCheckboxState: props.getSelectionCheckboxState,
    getSelectAllCheckboxState: props.getSelectAllCheckboxState,
    getSelectionState: props.getSelectionState,
    readOnly,
    role: props.role,
    ariaRoleDescription: props.ariaRoleDescription,
    singleSelectionOverrides: props.singleSelectionOverrides,
    hideSelectionControlColumn: props.hideSelectionControlColumn,
    onActivate: props.onActivate,
    onDoubleClick: props.onDoubleClick,
    doubleClickOnEnter: props.doubleClickOnEnter,
    disableOnDoubleClickKeyboardActivation: props.disableOnDoubleClickKeyboardActivation,
    hideSelectionControl: props.hideSelectionControl,
    deleteRowAnnouncement: props.deleteRowAnnouncement,
    orderedColumnKeys: getVisibleColumnKeys(),
    selectableOnlyBySelectionControl: props.selectableOnlyBySelectionControl,
    hideSelectAll: props.hideSelectAll,
    ...props.narration
  });
  const directSpeaker = useDirectSpeech({
    mode: 'assertive'
  });

  // pass the tableNarration handle to interested parent
  useImperativeHandle(props.narratorRef, () => ({
    announcePosition: () => {
      if (tableState.initialFocus !== undefined) {
        const text = tableNarration.createNarration(tableState.initialFocus);
        if (!isNullish(text)) {
          directSpeaker?.notify(text);
        }
      }
    },
    getPositionAnnouncementMessage: () => {
      if (tableState.initialFocus === undefined) return null;
      return tableNarration.createNarration(tableState.initialFocus);
    }
  }), [tableNarration, directSpeaker, tableState.initialFocus]);
  useImperativeHandle(props.tableAPIRef, () => ({
    getColumnIndexForOffset: scrollLeft => {
      let offset = 0;
      const frozenCount = props.preference?.frozenColumnCount ?? 0;
      for (let index = 0; index < frozenCount; ++index) {
        offset = refGrid.current.getCurrentColumnWidth(index) + offset;
      }
      return refGrid.current?.getColumnIndexForOffset(scrollLeft + offset);
    },
    getRowIndexForOffset(scrollTop) {
      return refGrid.current?.getRowIndexForOffset(scrollTop);
    },
    setSelectedRows: rowKeys => {
      const keys = props.selectionMode === 'single' ? new Set([rowKeys?.length > 0 ? rowKeys[0] : '']) : new Set(rowKeys);
      props.setSelectRows?.({
        selectedRows: keys,
        disabledRows: props.disabledRows
      });
    },
    setSelectAll: selectAll => {
      props.setSelectAllRows?.({
        selectAll
      });
      if (props.preference?.selectedRows?.size ?? 0 > 0) props.setSelectRows?.({
        selectedRows: undefined
      });
    },
    setColumnsVisiblity: columnsVisiblity => {
      const {
        preference,
        setOrderedColumnKeys
      } = props;
      const {
        orderedColumnKeys = []
      } = preference || {};
      let orderedArray = [...orderedColumnKeys];
      columnsVisiblity.forEach(columnStatus => {
        const {
          key,
          visible,
          visibleAtIndex
        } = columnStatus;
        if (visible && !orderedArray?.includes(key)) {
          const index = (visibleAtIndex !== undefined ? visibleAtIndex : colKeys.indexOf(key)) !== -1 ? visibleAtIndex !== undefined ? visibleAtIndex : colKeys.indexOf(key) : -1;
          if (index >= 0) {
            orderedArray.splice(index, 0, key);
          } else {
            orderedArray.push(key);
          }
        } else if (!visible) {
          orderedArray = orderedArray.filter(item => item !== key);
        }
      });
      setOrderedColumnKeys?.({
        orderedKeys: orderedArray
      });
    }
  }));
  const moveDropIndicator = useCallback((position, height) => {
    if (!dropIndicatorRef.current) return;
    dropIndicatorRef.current.style.top = `${position}px`;
    dropIndicatorRef.current.style.height = `${height ?? defaultSizes.dropIndicatorLineHeight}px`;
    dropIndicatorRef.current.style.display = position > -1 ? 'block' : 'none';
  }, []);
  useImperativeHandle(props.dndRef, () => ({
    displayDropIndicator: (index, clearEmptyZone) => {
      if ((props.rowCount || 0) < 1 && index === -1) {
        setDragging(false);
        setEmptyDropZone(clearEmptyZone ? false : true);
        return;
      }
      if (index === -1) {
        setDragging(false);
        setEmptyDropZone(false);
        return;
      }
      const row = Math.min((index ?? -1) + 1, props.rowCount + 1);
      const gridInternalRef = refGrid.current;
      setDragging(true);
      const cellStyle = gridInternalRef?._getRowStyle(row - (props.columnHeadersVisible === false ? 1 : 0), 1);
      const indicator = currentDropIndicator || props.dropZoneIndicator;
      const position = cellStyle?.top - gridInternalRef?.getScrollTop() - (indicator === 'zone' ? 0 : 5);
      moveDropIndicator(position, indicator === 'zone' ? cellStyle?.height : defaultSizes.dropIndicatorLineHeight);
    },
    hideDropIndicator: () => {
      setDragging(false);
      moveDropIndicator(-1);
      setEmptyDropZone(false);
      rowRef.current?.updateContent();
    }
  }), [props.rowCount, props.columnHeadersVisible, props.dropZoneIndicator, refGrid, currentDropIndicator, moveDropIndicator]);
  const fillerWidth = getColumnFillerWidth();
  const getColumnWidth = useCallback((columnKey, columnIndex) => {
    // There is a fillerWidth - autoFillLastColumn is true and columns don't fill the viewport
    if (fillerWidth > 0 && measuredColumns && columnIndex >= measuredColumns?.size) {
      return fillerWidth;
    }
    let width = props.preference?.resizedColumns?.has(columnKey) ? props.preference?.resizedColumns?.get(columnKey) : measuredColumns.get(columnKey);
    const minWidth = columns[columnIndex]?.minimumWidth ?? defaultSizes.minimumColumnWidth;
    // NOTE: typescript doesn't seem to be able to narrow past the indexer
    if (!isNullish(width)) {
      return Math.max(width, minWidth);
    }
    width = columns[columnIndex]?.width;
    if (!isNullish(width)) {
      return Math.max(width, minWidth);
    }
    const widthCallback = props.columnWidth;
    if (widthCallback) {
      return Math.max(widthCallback(columnIndex, columnKey), minWidth);
    }
    return Math.max(defaultSizes.columnWidth, minWidth);
  }, [columns, props.preference?.resizedColumns, props.columnWidth, measuredColumns, fillerWidth]);
  const resetFrozenColumnIfUnreachable = useCallback(count => {
    const orderedColumnKeys = props.preference?.orderedColumnKeys;
    if (!count || !orderedColumnKeys) return false;
    let frozenWidth = defaultSizes.minimumUnfrozenColumnWidth;
    let unfreezeCount = 0;
    const selection = selectionMode || 'multiple';
    const hasSelColumn = hasSelectionColumn(selection, columns.length, props.rowCount, props.hideSelectionControlColumn);
    for (let index = hasSelColumn ? 1 : 0; index < (hasSelColumn ? count + 1 : count); ++index) {
      const column = columns.find(e => e.key === orderedColumnKeys[index]);
      frozenWidth += column ? getColumnWidth(column?.key, index) || defaultSizes.columnWidth : 0;
      unfreezeCount += column?.freezeable === false ? 1 : 0;
    }
    if (hasSelColumn) frozenWidth += defaultSizes.selectionControlColumnWidth;
    if (frozenWidth >= (props.expandToFillWidth && dimensions ? dimensions.width : props.width ?? defaultSizes.tableWidth)) {
      interactiveActionRef.current = true;
      const setFreezeColumnCount = props.setFreezeColumnCount;
      setFreezeColumnCount?.({
        count: unfreezeCount || 0
      });
      const currentGrid = refGrid.current;
      currentGrid.resetAfterColumnIndex(0);
      return true;
    }
    return false;
  }, [props.preference?.orderedColumnKeys, props.rowCount, props.hideSelectionControlColumn, props.expandToFillWidth, props.width, props.setFreezeColumnCount, selectionMode, columns, dimensions, getColumnWidth, refGrid]);
  const isCloseContextMenuRef = useRef(false);
  const updatingContextMenuRef = useRef();
  const closeContextMenu = useCallback(focusIndicator => {
    if (!mounted.current) return;
    setContextMenuOpen(false);
    topRef.current?.focus({
      preventScroll: true
    });
    isCloseContextMenuRef.current = true;
    dispatch(TableState.actions.createFocusIndicator({
      focusIndicator: focusIndicator ?? previousFocusIndicator
    }));
    updatingContextMenuRef.current = undefined;
    setContextMenuItems(undefined);
  }, [previousFocusIndicator, topRef, mounted]);
  const onSort = useCallback((event, columnKey, allowPropagation) => {
    !allowPropagation && event.stopPropagation();
    closeContextMenu('visible');
    const column = columns.find(({
      key
    }) => key === columnKey);
    if (!column || event.isDefaultPrevented()) return;
    setKeyboardResizeColumn(undefined);
    const onSort = props.onSort;
    const hasPrimarySort = columns.some(col => col.sortDirection && col.sortDirection !== 'data');
    const direction = !column.sortDirection || column.sortDirection === 'descending' ? 'ascending' : 'descending';
    const isPrimarySortAction = !(event.ctrlKey && column.sortLevel !== 1 && props.allowSecondarySort && hasPrimarySort);
    onSort?.({
      columnKey,
      direction,
      action: getSortAction({
        column,
        direction,
        isPrimarySortAction,
        ctrlKey: event.ctrlKey
      })
    });
  }, [closeContextMenu, columns, props.onSort, props.allowSecondarySort]);

  /**
   * This function is passed along to all header and data cells to help those cells determine if they should show the context area.
   */
  const contextMenuInfo = contextMenuInfoRef.current;
  const showContextArea = useCallback((row, column) => {
    // 1. never show context area if context menu is not visible
    if (!isContextMenuOpen) {
      return false;
    }
    const cell = contextMenuInfo?.contextInfo.cell;
    // header rows are inconsistent between -1 and undefined
    const doesRowKeyMatch = cell?.row === row || row === undefined && cell?.row === '-1';
    const doKeysMatch = doesRowKeyMatch && cell?.column === column;
    // 2. show context area only if the row and column keys match
    if (!doKeysMatch) {
      return false;
    }
    const isHeaderCell = contextMenuInfo?.position?.rowIndex === -1;
    const showContext = isHeaderCell || !props.showContextForRow ? doKeysMatch : false;
    return showContext;
  }, [isContextMenuOpen, contextMenuInfo, props.showContextForRow]);
  const buildCellBorderStyles = useCallback(row => {
    const borderStyles = [];
    if (isContextMenuOpen && props.showContextForRow === true && contextInfo.cell?.row === row) {
      borderStyles.push(cn.cellSelectionBorder);
    }
    return borderStyles;
  }, [isContextMenuOpen, contextInfo.cell, props.showContextForRow]);
  const updateContextMenuItems = (contextMenu, cell) => {
    updatingContextMenuRef.current = cell;
    if (contextMenu?.updateContextMenuItems && cell) {
      contextMenu?.updateContextMenuItems(cell).then(value => {
        if (updatingContextMenuRef.current === cell) setContextMenuItems(value);
      });
    }
    setContextMenuItems(contextMenu);
  };
  const getSortingLabel = useCallback(props => {
    const numberOfColumnsSorted = props.columns.reduce((acc, column) => {
      if (column.sortDirection === 'ascending' || column.sortDirection === 'descending') {
        return acc + 1;
      }
      return acc;
    }, 0);
    const thisColumnIsSorted = props.column.sortDirection === 'ascending' || props.column.sortDirection === 'descending';
    if (props.allowSecondarySort && numberOfColumnsSorted > 0 && !thisColumnIsSorted) {
      return props.direction === 'ascending' ? localize('sort.addToSortingAscending.label') : localize('sort.addToSortingDescending.label');
    }
    if (props.allowSecondarySort && numberOfColumnsSorted > 1 && thisColumnIsSorted) {
      return props.direction === 'ascending' ? localize('sort.addToSortingAscendingSortOrder.label', {
        sortLevel: props.column.sortLevel?.toString(),
        totalSortedColumns: numberOfColumnsSorted.toString()
      }) : localize('sort.addToSortingDescendingSortOrder.label', {
        sortLevel: props.column.sortLevel?.toString(),
        totalSortedColumns: numberOfColumnsSorted.toString()
      });
    }
    return props.direction === 'ascending' ? localize('sort.ascending.label') : localize('sort.descending.label');
  }, [localize]);
  const headerContextMenu = useCallback((column, columnIndex, hasSelectionControl) => {
    if (props.disabled || !column) return undefined;
    const menuItemIdentifiers = [];
    const items = [];
    const numOfColumns = hasSelectionControl ? columns.length - 1 : columns.length;
    const actualColumnIndex = columns.findIndex(col => col.key === column.key);
    const isLastColumn = actualColumnIndex === columns.length - 1;
    // do not show the resize menu item if there is only one column
    // or if this is the last column
    column.resizable !== false && numOfColumns > 1 && !isLastColumn && menuItemIdentifiers.push('RESIZE');
    if (numOfColumns > 1 && column.freezeable !== false) {
      const frozenCount = (props.preference?.frozenColumnCount ?? 0) + (hasSelectionControl ? 1 : 0);
      frozenCount > columnIndex ? menuItemIdentifiers.push('UNFREEZE') : menuItemIdentifiers.push('FREEZE');
    }
    column.sortable && menuItemIdentifiers.push('SORT');
    const aColumnIsSorted = columns.some(column => column.sortDirection === 'ascending' || column.sortDirection === 'descending');
    menuItemIdentifiers.forEach(identifier => {
      switch (identifier) {
        case 'RESIZE':
          const currentGrid = refGrid.current;
          setInitialColumnWidth(currentGrid?.getCurrentColumnWidth(actualColumnIndex));
          items.push({
            text: localize('resize.menuItem.label')
          });
          break;
        case 'UNFREEZE':
          items.push({
            text: localize('unfreeze.menuItem.label')
          });
          break;
        case 'FREEZE':
          items.push({
            text: localize('freeze.menuItem.label')
          });
          break;
        case 'SORT':
          const columnIsNotSorted = column.sortDirection === 'data' || !column.sortDirection;
          const selectedIndex = column.sortDirection === 'ascending' ? 0 : column.sortDirection === 'descending' ? 1 : -1;
          const ascendingLabel = getSortingLabel({
            direction: 'ascending',
            allowSecondarySort: props.allowSecondarySort,
            column,
            columns
          });
          const descendingLabel = getSortingLabel({
            direction: 'descending',
            allowSecondarySort: props.allowSecondarySort,
            column,
            columns
          });
          const sortItems = {
            selectedIndex,
            items: [{
              text: ascendingLabel
            }, {
              text: descendingLabel
            }]
          };
          items.push({
            text: localize('sort.menuItem.label'),
            subSections: [sortItems, {
              items: [{
                text: localize('sort.removeSort.label'),
                disabled: columnIsNotSorted
              }, ...(props.allowSecondarySort ? [{
                text: localize('sort.removeAllSort.label'),
                disabled: props.allowSecondarySort && !aColumnIsSorted
              }] : [])]
            }]
          });
          break;
      }
    });

    // any custom sections given for this column
    let addedSections = column.additionalHeaderContextMenuProps?.sections ?? [];
    // any custom items given for this column
    const addedItems = column.additionalHeaderContextMenuProps?.items ?? [];

    // add custom items as 1st custom section
    if (addedItems.length > 0) {
      addedSections = [{
        items: addedItems
      }, ...addedSections];
    }
    const hasDefaultItems = items.length > 0;
    const hasCustomItems = addedSections.length > 0;

    // gather all sections together - default followed by custom items then custom sections
    const allSections = hasDefaultItems ? [{
      items
    }, ...addedSections] : addedSections;

    // check if there are any menu items at all
    if (allSections.length === 0) return undefined;
    return {
      onDismiss: () => {
        closeContextMenu();
      },
      sections: allSections,
      onItemSelected: location => {
        const [sectionIndex, itemIndex] = location[0];
        if (hasDefaultItems && sectionIndex === 0) {
          const selectedIndex = itemIndex;
          const menuAction = menuItemIdentifiers[selectedIndex];
          const currentGrid = refGrid.current;
          switch (menuAction) {
            case 'SORT':
              {
                updateFauxcus({
                  region: TableRegions.ColumnHeader,
                  colIndex: columnIndex + (hasSelectionControl ? 1 : 0),
                  rowIndex: -1
                });
                const onSort = props.onSort;
                // if this is 0 the user clicked Ascending or Descending
                // if this is 1 the user clicked Remove sorting or Remove all sorting
                const sortMenuSection = location[1][0];
                // sortMenuSection 0 and sortMenuIndex === 0 'Ascending', sortMenuIndex === 1 'Descending'
                // sortMenuSection 1 and sortMenuIndex === 0 'Remove sorting', sortMenuIndex === 1 'Remove all sorting'
                const sortMenuIndex = location[1][1];
                const isPrimarySortAction = !aColumnIsSorted || column.sortLevel === 1 || !props.allowSecondarySort;
                if (sortMenuSection === 0) {
                  const direction = sortMenuIndex === 0 ? 'ascending' : sortMenuIndex === 1 ? 'descending' : 'data';
                  onSort?.({
                    columnKey: column.key,
                    direction,
                    action: getSortAction({
                      column,
                      direction,
                      isPrimarySortAction
                    })
                  });
                }
                if (sortMenuSection === 1) {
                  onSort?.({
                    columnKey: column.key,
                    direction: 'data',
                    action: sortMenuIndex === 0 ? 'remove' : 'removeAll'
                  });
                }
                break;
              }
            case 'FREEZE':
              {
                const previousFrozenColumnCount = props.preference?.frozenColumnCount ?? 0;
                interactiveActionRef.current = true;
                const setFreezeColumn = props.setFreezeColumn;
                setFreezeColumn?.({
                  columnKey: column.key,
                  unmanagedColumnKeys: props.unmanagedColumnKeys,
                  orderedColumnKeys: props.preference?.orderedColumnKeys
                });
                resetFrozenColumnIfUnreachable(previousFrozenColumnCount + 1);
                currentGrid.resetAfterColumnIndex(0);
                break;
              }
            case 'UNFREEZE':
              {
                interactiveActionRef.current = true;
                const setFreezeColumn = props.setFreezeColumn;
                setFreezeColumn?.({
                  columnKey: column.key,
                  unfreeze: true,
                  orderedColumnKeys: props.preference?.orderedColumnKeys
                });
                currentGrid.resetAfterColumnIndex(0);
                break;
              }
            case 'RESIZE':
              updateFauxcus({
                region: TableRegions.ColumnHeader,
                colIndex: actualColumnIndex,
                rowIndex: -1
              });
              mounted.current && dispatch(TableState.actions.createFocusIndicator({
                focusIndicator: 'visible'
              }));

              // narrate beginning of column resize
              directSpeaker?.notify(tableNarration.getColumnWidthStartNarration());
              setKeyboardResizeColumn(column.key);
              break;
          }
          return;
        }

        // call the custom onItemSelected handler, if custom item has been selected
        if (hasCustomItems) {
          column.additionalHeaderContextMenuProps?.onItemSelected?.(location);
        }
      }
    };
  }, [props.disabled, props.preference?.frozenColumnCount, props.preference?.orderedColumnKeys, props.allowSecondarySort, props.onSort, props.setFreezeColumn, props.unmanagedColumnKeys, columns, refGrid, localize, getSortingLabel, closeContextMenu, updateFauxcus, mounted, directSpeaker, tableNarration, resetFrozenColumnIfUnreachable]);
  const isColumnResizing = useCallback(column => {
    if (column === keyboardResizeColumn) {
      return true;
    }
    return false;
  }, [keyboardResizeColumn]);
  const resizeColumn = useCallback((deltaPX, columnIndex, reset) => {
    const column = columns[columnIndex];
    if (props.disabled || column.resizable === false) return;
    const currentGrid = refGrid.current;
    let columnWidth;
    if (reset === true) columnWidth = initialColumnWidth;else {
      const minwidth = column.minimumWidth || defaultSizes.minimumColumnWidth;
      const maxwidth = column.maximumWidth || defaultSizes.maximumColumnWidth;
      columnWidth = currentGrid?.getCurrentColumnWidth(columnIndex);
      if (columnWidth < minwidth && deltaPX < 0) return;
      if (columnWidth > maxwidth && deltaPX > 0) return;
    }
    const previousFrozenColumnCount = props.preference?.frozenColumnCount ?? 0;
    const setResizedColumn = props.setResizedColumn;
    const newWidth = Math.max(columnWidth + deltaPX, defaultSizes.minimumColumnWidth);
    setResizedColumn && setResizedColumn({
      columnKey: column.key,
      width: newWidth
    });
    directSpeaker?.notify(tableNarration.getColumnWidthChangeNarration(newWidth.toString()));
    resetFrozenColumnIfUnreachable(previousFrozenColumnCount);
    // to adjust the width and force a refresh
    currentGrid?._setColumnWidth(columnIndex, newWidth);
    if (props.fireResizeChangeWithDrag === true) {
      const onColumnResize = props.onColumnResize;
      onColumnResize?.(column.key, newWidth);
    }
  }, [columns, props.disabled, props.preference?.frozenColumnCount, props.setResizedColumn, props.fireResizeChangeWithDrag, props.onColumnResize, refGrid, initialColumnWidth, directSpeaker, tableNarration, resetFrozenColumnIfUnreachable]);
  const managableColumnKeys = () => {
    const omitColumns = props.unmanagedColumnKeys;
    if (!omitColumns) return displayedColumnKeys;
    const managed = displayedColumnKeys;
    const managedColumns = managed?.filter(key => {
      return !omitColumns.includes(key);
    });
    return managedColumns;
  };
  const managableColumns = () => {
    const omitColumns = props.unmanagedColumnKeys;
    if (!omitColumns) return props.columns || [];
    const managed = props.columns;
    const managedColumns = managed?.filter(column => {
      return !omitColumns.includes(column.key);
    });
    return managedColumns || [];
  };
  const controlsRefs = new Map();
  const updateControlRefs = (columnKey, rowKey, ref) => {
    // find column
    const columnRefs = controlsRefs.get(columnKey);
    if (!columnRefs) {
      const rows = new Map();
      rows.set(rowKey, ref);
      controlsRefs.set(columnKey, rows);
    } else {
      if (columnRefs.has(rowKey)) columnRefs.delete(rowKey);
      columnRefs.set(rowKey, ref);
    }
  };
  const helpRefs = new Map();
  const updateHelpRefs = (columnKey, rowKey, ref) => {
    // find column
    const columnRefs = helpRefs.get(columnKey);
    if (!columnRefs) {
      const rows = new Map();
      rows.set(rowKey, ref);
      helpRefs.set(columnKey, rows);
    } else {
      if (columnRefs.has(rowKey)) columnRefs.delete(rowKey);
      columnRefs.set(rowKey, ref);
    }
  };
  const activateRefs = new Map();
  const updateActivateRefs = (columnKey, rowKey, ref) => {
    // find column
    const columnRefs = activateRefs.get(columnKey);
    if (!columnRefs) {
      const rows = new Map();
      rows.set(rowKey, ref);
      activateRefs.set(columnKey, rows);
    } else {
      if (columnRefs.has(rowKey)) columnRefs.delete(rowKey);
      columnRefs.set(rowKey, ref);
    }
  };
  const getControlRef = (columnKey, rowKey) => {
    if (columnKey === '' || !controlsRefs.has(columnKey)) return undefined;
    const columnRefs = controlsRefs.get(columnKey);
    if (columnRefs === undefined) return undefined;
    return columnRefs.get(rowKey);
  };
  const getHelpRef = (columnKey, rowKey) => {
    if (columnKey === '' || !helpRefs.has(columnKey)) return undefined;
    const columnRefs = helpRefs.get(columnKey);
    if (columnRefs === undefined) return undefined;
    return columnRefs.get(rowKey);
  };
  const getActivateRef = (columnKey, rowKey) => {
    if (columnKey === '' || !activateRefs.has(columnKey)) return undefined;
    const columnRefs = activateRefs.get(columnKey);
    if (columnRefs === undefined) return undefined;
    return columnRefs.get(rowKey);
  };
  const isInteractiveControl = props.isInteractiveControl;
  const itemData = {
    ...tableState,
    renderCell: props.renderCell || Defaults.renderCell,
    isInteractiveControl,
    columns,
    getContextMenuInfo: () => contextMenuInfoRef.current,
    setContextMenuInfo: info => {
      contextMenuInfoRef.current = info;
    },
    getEditState: () => editState,
    cancelEditingCell: () => {
      setEditState({
        isEditing: false
      });
    },
    onSort,
    setInteractiveAction,
    selectionMode: selectionMode || 'multiple',
    hideSelectAll: props.hideSelectAll,
    singleSelectionOverrides: props.singleSelectionOverrides,
    hideManageColumnsButton: props.hideManageColumnsButton,
    rowCount: props.rowCount,
    columnHeadersVisible: isColumnHeaderVisible,
    cellContextMenu: props.cellContextMenu,
    getRowIdentifier,
    getRowIndex: props.getRowIndex,
    storePreviousFocusIndicator: setPreviousFocusIndicator,
    updateFocusedCell: updateFauxcus,
    updateContextMenuStatus: setContextMenuOpen,
    showContextArea,
    showContextForRow: props.showContextForRow ?? false,
    buildCellBorderStyles,
    refGrid,
    refTop: topRef,
    onColumnResize: props.onColumnResize,
    setContextMenuOpen,
    updateContextMenuItems,
    headerContextMenu,
    isColumnResizing,
    sortRef,
    setSortColumn,
    resizeColumn,
    hasContextMenu: () => props.cellContextMenu !== undefined,
    onDoubleClick,
    disableClickDelay: props.disableClickDelay,
    localize,
    updateControlRefs,
    updateHelpRefs,
    updateActivateRefs,
    getControlRef,
    getHelpRef,
    getActivateRef,
    getSelectionCheckboxState: props.getSelectionCheckboxState,
    getSelectAllCheckboxState: props.getSelectAllCheckboxState,
    getSelectionState: props.getSelectionState,
    areRowsVisible: props.areRowsVisible,
    visibleStartRowIndex: 0,
    visibleStopRowIndex: 10,
    preference: props.preference,
    onSelect: props.onSelect,
    onActivate,
    // setters to update preference
    setSelectRow: props.setSelectRow,
    setSelectRows: props.setSelectRows,
    setSelectAllRows: props.setSelectAllRows,
    setFreezeColumn: props.setFreezeColumn,
    setFreezeColumnCount: props.setFreezeColumnCount,
    showSeparator: props.showSeparator,
    autoFillLastColumn: props.autoFillLastColumn,
    hideFillRows: props.hideFillRows,
    removeFillRows: props.removeFillRows,
    selectableOnlyBySelectionControl: hasSelectionColumn(selectionMode, props.columns?.length ?? 0, props.rowCount, props.hideSelectionControlColumn) && props.selectableOnlyBySelectionControl,
    hideUnselectedSelectionControl: props.hideUnselectedSelectionControl,
    disabled: props.disabled,
    isRowDisabled,
    isRowReadOnly,
    hideSelectionControl: props.hideSelectionControl,
    readOnly,
    dragging,
    dragEnabled: props.dragEnabled,
    dropEnabled: props.dropEnabled,
    hideSelectionControlColumn: props.hideSelectionControlColumn,
    getViewPortWidth,
    draggingRow,
    onCellMouseEnter: props.onCellMouseEnter,
    onCellFocus: props.onCellFocus,
    disabledRows: props.disabledRows,
    disabledAndSelectedRows: props.disabledAndSelectedRows,
    readOnlyAndSelectedRows: props.readOnlyAndSelectedRows
  };
  const sortDirections = columns?.map(({
    sortDirection
  }) => sortDirection).join(',') ?? '';
  useEffect(() => {
    const anchor = itemData?.preference?.selectionAnchor;
    const endIndex = selectionEndIndex.current;
    if (needToAnnounceAddedSelectionRange.current) {
      if (anchor !== undefined && endIndex !== undefined) directSpeaker?.notify(tableNarration.createRangeAddedSelectionNarration(anchor + 1, endIndex + 1));
    } else if (needToAnnounceTotalSelectionRange.current) {
      if (anchor !== undefined && endIndex !== undefined) directSpeaker?.notify(tableNarration.createTotalSelectionNarration(anchor + 1, endIndex + 1));
    }
    needToAnnounceTotalSelectionRange.current = false;
    needToAnnounceAddedSelectionRange.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemData.preference?.selectionAnchor, needToAnnounceTotalSelectionRange.current, needToAnnounceAddedSelectionRange.current]);

  /*
  // the next block will be based on the focus state within table.
  // it will attempt to scroll the focused item into view (in case it is not currently).
  // it will make a DirectSpeech announcement based on the current and last focused cell.
  */
  useEffect(() => {
    if (!tableState.initialFocus) {
      return;
    }
    const focused = tableState.focusIndicator === undefined && focusVisible;
    const initialFocus = tableState.initialFocus;
    const lastFocusedCell = tableState.lastFocusedCell;
    const focusChanged = initialFocus.rowIndex !== lastFocusedCell?.rowIndex || initialFocus.colIndex !== lastFocusedCell?.colIndex;
    if (initialFocus.region !== lastFocusedCell?.region) {
      const focusOnZeroState = document.activeElement && document.activeElement.closest(`.${zeroStateCn.zeroState}`) !== null;
      if (tableState.initialFocus?.region === TableRegions.ZeroStateOverlay && !focusOnZeroState && !props.disableAutoFocus) {
        if (shouldShowZeroState) {
          focusFirstZeroStateElement();
        } else {
          topRef.current?.focus();
        }
      }
    }

    // scroll to the appropriate cell if we're in the data or header region
    if ((tableState.focusIndicator === 'visible' || focused) && focusChanged) {
      if (tableState.initialFocus?.region === TableRegions.Data)
        // if region is data, scroll to the given row and column
        refGrid.current?.scrollToItem({
          align: 'smart',
          rowIndex: tableState.initialFocus?.rowIndex,
          columnIndex: tableState.initialFocus?.colIndex
        });else if (tableState.initialFocus?.region === TableRegions.ColumnHeader)
        // if region is column header, scroll to the given column
        // do not scroll to a different row because column header is always visible
        refGrid.current?.scrollToItem({
          align: 'smart',
          columnIndex: tableState.initialFocus?.colIndex
        });else if (tableState.initialFocus?.region === TableRegions.RowHeader)
        // if region is row header, scroll to the given row
        // do not scroll to a different column because row header is always visible
        refGrid.current?.scrollToItem({
          align: 'smart',
          rowIndex: tableState.initialFocus?.rowIndex
        });
    }
    if (tableState.focusIndicator !== 'unfocused' && tableState.focusIndicator !== undefined && (initialFocus.rowIndex !== lastFocusedCell?.rowIndex || initialFocus.colIndex !== lastFocusedCell?.colIndex || initialFocus.region !== lastFocusedCell?.region)) {
      const message = tableNarration.createNarration(initialFocus, lastFocusedCell);
      if (message !== null) {
        directSpeaker?.notify(message);
      }
      tableState.lastFocusedCell = initialFocus;
    }
    // We are making an exception to the lint rule in this case
    // because it wants to see tableState as a dependency.
    // There are many properties on tableState and we only
    // use a few of them in this hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.initialFocus, tableState.lastFocusedCell, tableState.focusIndicator, keyboardResizeColumn, tableNarration, directSpeaker]);

  // scan columns and look for variations in widths that should reset metadata
  useEffect(() => {
    const currentGrid = itemData.refGrid.current;
    const setResizedColumn = props.setResizedColumn;
    if (!currentGrid) return;
    for (let c = 0, cend = itemData.columns.length; c < cend; c++) {
      const column = itemData.columns[c];
      const columnWidth = currentGrid.getCurrentColumnWidth(c);
      const minwidth = column.minimumWidth || defaultSizes.minimumColumnWidth;
      const maxwidth = column.maximumWidth || defaultSizes.maximumColumnWidth;
      // enforce min/max column width
      const measuredWidth = measuredColumns.get(column.key) || column.width;
      const calcWidth = Math.max(minwidth, Math.min(maxwidth, measuredWidth ?? defaultSizes.columnWidth));
      if (column.width !== undefined && columnWidth !== calcWidth && props.preference?.resizedColumns?.get(column.key) !== measuredWidth) {
        const minColWidth = column.minimumWidth ?? defaultSizes.minimumColumnWidth;
        setResizedColumn && setResizedColumn({
          columnKey: column.key,
          width: Math.max(calcWidth, minColWidth)
        });
        currentGrid && currentGrid.resetAfterColumnIndex(c);
      }
    }
  }, [itemData.columns, itemData.refGrid, measuredColumns, props.preference?.resizedColumns, props.setResizedColumn]);
  useEffect(() => {
    if (tableState.initialFocus && tableState.focusIndicator !== undefined && tableState.focusIndicator !== 'unfocused') {
      const message = tableNarration.createNarration(tableState.initialFocus);
      if (message !== null) {
        directSpeaker?.notify(message);
      }
    }
    // We are making an exception to the lint rule in this case
    // because it wants to see tableState.initialFocus as a dependency.
    // There is already a useEffect watching tableState.initialFocus
    // and we do not want to duplicate that effort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirections, props.preference?.frozenColumnCount]);
  useEffect(() => {
    const currentGrid = itemData.refGrid.current;
    props.expandToFillWidth && currentGrid && currentGrid.resetAfterColumnIndex(0);
  }, [dimensions, props.expandToFillWidth, itemData.refGrid]);
  useEffect(() => {
    if (!didMount.current) {
      const setOrderedColumnKeys = props.setOrderedColumnKeys;
      setOrderedColumnKeys?.({
        orderedKeys: getVisibleColumnKeys()
      });
    }
  }, [props.setOrderedColumnKeys, initialOrderedColumnKeys, props.onFrozenColumnCountChange, props.onOrderedColumnsChange, getVisibleColumnKeys]);

  // NOVACOMMONS-13275: suppress vertical scrolling when nav keys pressed while editing cell content
  useEffect(() => {
    const columnNavKeySuppressionList = editState.isEditing ? editState.column.editorNavKeySuppressionList : undefined;
    const customNavKeySuppressionList = typeof columnNavKeySuppressionList === 'string' ? [columnNavKeySuppressionList] : columnNavKeySuppressionList;
    const keyHandler = event => {
      if (!editState.isEditing || event.defaultPrevented) {
        return;
      }

      /*
       * General rule - prevent default and stop propagation on keydown events for the following keys:
       *  'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'
       * Exceptions: some elements require different rules
       * - Anchor - add 'Space'
       * - Input - only 'PageUp' and 'PageDown'
       * - TextArea - only 'PageUp' and 'PageDown'
       */
      let navKeyList = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
      if (!!customNavKeySuppressionList) {
        // allow consumer to customize suppression list (including empty array)
        navKeyList = customNavKeySuppressionList;
      } else if (event.target instanceof HTMLInputElement) {
        navKeyList = ['PageUp', 'PageDown'];
      } else if (event.target instanceof HTMLTextAreaElement) {
        navKeyList = ['PageUp', 'PageDown'];
      } else if (event.target instanceof HTMLAnchorElement) {
        navKeyList = ['Space', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
      }
      if (navKeyList.includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('keydown', keyHandler);
    return () => {
      // clean up the event listener
      document.removeEventListener('keydown', keyHandler);
    };
  }, [editState]);
  useEffect(() => {
    if (shouldShowZeroState && editState.isEditing) {
      setEditState({
        isEditing: false
      });
    }
  }, [shouldShowZeroState, editState.isEditing]);
  useEffect(() => {
    didMount.current = true;
    return () => {
      didMount.current = false;
    };
  }, []);
  const [prevWidth, setPrevWidth] = useState(dimensions?.width);
  useEffect(() => {
    let width;
    if (props.expandToFillWidth && topRef.current) {
      // calculations from useParentHook
      width = topRef.current.offsetWidth;
      const style = window.getComputedStyle(topRef.current) || {};
      width -= parseInt(style.paddingLeft, 10) || 0;
      width -= parseInt(style.paddingRight, 10) || 0;
    }
    if (!dimensions || dimensions?.width === prevWidth || width !== dimensions?.width // wait until expanded
    ) {
      return;
    }
    setPrevWidth(dimensions?.width);
    const count = props.preference?.frozenColumnCount ?? 0;
    if (count && count > 0) resetFrozenColumnIfUnreachable(count);
  }, [dimensions, prevWidth, props.expandToFillWidth, props.preference?.frozenColumnCount, resetFrozenColumnIfUnreachable, topRef]);

  /*
   * @param isHeader boolean indicating if the context menu is for the header
   * @param rowIndex
   * @param colIndex
   * @returns the screen x/y position for the context menu
   */
  const getContextMenuPosition = (isHeader, rowIndex, colIndex) => {
    const gridInternalRef = refGrid.current;
    const cellStyle = gridInternalRef?._getRowStyle(isHeader ? 0 : rowIndex, colIndex);
    const yBase = isHeader ? headerHeight : cellStyle.top + getRowHeight(rowIndex) + headerHeight;
    return {
      x: isRTL ? (topRef.current ? topRef.current?.getBoundingClientRect().left + topRef.current?.offsetWidth : 0) - cellStyle.left + gridInternalRef?.getScrollLeft() : cellStyle.left + (topRef.current ? topRef.current?.getBoundingClientRect().left : 0) - gridInternalRef?.getScrollLeft(),
      y: yBase + (topRef.current ? topRef.current?.getBoundingClientRect().top : 0) - gridInternalRef?.getScrollTop()
    };
  };

  // called to configure the context menu to display items
  const triggerContextMenu = info => {
    const rowKey = info.contextInfo?.cell?.row ?? '';
    if (itemData.disabled || rowKey && !!isRowDisabled?.(rowKey) || rowKey && !!isRowReadOnly?.(rowKey)) {
      return;
    }
    const {
      colIndex,
      rowIndex
    } = info.position;
    const doesHaveSelectionColumn = hasSelectionColumn(itemData.selectionMode, itemData.columns.length, itemData.rowCount, itemData.hideSelectionControlColumn);
    const index = colIndex - (doesHaveSelectionColumn ? 1 : 0);
    const contextMenuInfo = itemData.getContextMenuInfo();
    const column = info.column ?? contextMenuInfo?.column ?? columns[colIndex];
    const activeCell = info.position ?? tableState.activeCell ?? contextMenuInfo?.position;
    const prevRow = activeCell?.rowIndex;
    const isHeader = prevRow === -1;

    // cannot show the context menu if the column index is -1 (e.g. the manage columns button)
    if (colIndex === -1) return;

    // do not show the context menu on the select all checkbox for multiple selection
    if (isHeader && itemData.selectionMode === 'multiple' && index === -1) return;
    const headerContext = isHeader && columns ? headerContextMenu(column, colIndex, doesHaveSelectionColumn) : undefined;
    if (isHeader && headerContext === undefined) return;
    if (isHeader && columns) {
      updateContextMenuItems(headerContext);
    } else {
      updateContextMenuItems(props.cellContextMenu, {
        rowKey: rowKey || '',
        columnKey: column.key
      });
    }
    setPreviousFocusIndicator(tableState.focusIndicator);
    setContextInfo({
      position: getContextMenuPosition(isHeader, rowIndex, colIndex),
      cell: {
        row: rowKey,
        column: column.key
      }
    });
    setContextMenuOpen(true);
  };
  const [rowIntoView, setRowIntoView] = useState(null);
  const parentScrollRowIntoView = useCallback(() => {
    if (!mounted.current || rowIntoView === null || rowIntoView > -1 && !rowRefs[rowIntoView]) return;
    const isScrollable = elem => elem.scrollHeight > elem.clientHeight && window.getComputedStyle(elem).overflowY.indexOf('hidden') === -1;
    const getScrollableParent = elem => !elem || elem === document.body ? document.body : isScrollable(elem) ? elem : getScrollableParent(elem.parentNode);
    const isElementVisible = element => {
      const itemRect = element?.getBoundingClientRect();
      const parentRect = getScrollableParent(topRef?.current)?.getBoundingClientRect();
      return !itemRect || !parentRect ? false : itemRect.top >= parentRect.top && itemRect.bottom <= parentRect.bottom;
    };
    const element = rowIntoView === -1 ? headerRef.current : rowRefs[rowIntoView];
    if (element && !isElementVisible(element)) element?.scrollIntoView?.({
      block: 'nearest'
    });
    setRowIntoView(null);
  }, [rowRefs, headerRef, topRef, mounted, rowIntoView, setRowIntoView]);
  useEffect(() => {
    parentScrollRowIntoView();
  }, [parentScrollRowIntoView]);
  const navigationColumn = useRef(null);
  const isHandlingEnterKeyPress = useRef(false);
  const keyDownHandler = event => {
    const target = event.target;
    const role = target.getAttribute('role');
    directSpeechTableTextRef.current = null;
    const keyDownOnZeroState = document.activeElement && document.activeElement.closest(`.${zeroStateCn.zeroState}`) !== null;
    // on zero state, we only need to handle the tab key.
    // we will let the escape key fall through so it can close a dialog
    // if the table is in a dialog
    if (keyDownOnZeroState) {
      if (event.key === 'Escape') return;else if (event.key !== 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
    const isHelpActive = document.activeElement && document.activeElement.closest(`.${helpCn.container}`) !== null;
    if (topRef.current !== document.activeElement && isHelpActive) return;
    tableState.activeCell = tableState.initialFocus;
    const {
      rowCount
    } = props;
    const colCount = columns?.length ?? 0;
    let region = tableState.initialFocus?.region ?? TableRegions.Data,
      rowIndex = tableState.initialFocus?.rowIndex ?? 0,
      colIndex = tableState.initialFocus?.colIndex ?? 0;
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageDown', 'PageUp', 'ArrowLeft', 'ArrowRight'];
    if (tableState.focusIndicator !== 'visible') navigationColumn.current = null;
    if (navigationKeys.includes(event.key)) {
      if (navigationColumn.current) colIndex = navigationColumn.current;
      navigationColumn.current = null;
    }
    const columnKey = columns[colIndex]?.key;
    const inResizeMode = keyboardResizeColumn !== undefined;
    const hasSelectionCol = hasSelectionColumn(itemData.selectionMode, colCount, rowCount, props.hideSelectionControlColumn);
    const firstDataColIndex = hasSelectionCol ? 1 : 0;
    const singleSelection = itemData.selectionMode === 'single' || itemData.selectionMode === 'singleRadio';
    const validResizeKeys = ['ArrowLeft', 'ArrowRight', 'Enter', 'Escape'];
    if (!validResizeKeys.includes(event.key)) {
      setKeyboardResizeColumn(undefined);
    }
    const validEditKeys = ['Escape', 'F2'];
    // if we're in edit mode and this is not one of the keys to exit edit mode, just return
    if (editState.isEditing && !validEditKeys.includes(event.key)) return;
    const getRegionForCellPosition = () => {
      if (rowIndex < 0 && colIndex < 0) {
        return TableRegions.ColumnHeaderButton;
      } else if (colIndex < firstDataColIndex) {
        return rowIndex < 0 ? TableRegions.ColumnRowHeader : TableRegions.RowHeader;
      } else if (rowIndex < 0) {
        return TableRegions.ColumnHeader;
      }
      return TableRegions.Data;
    };
    const selectionType = (() => {
      if (getRegionForCellPosition() === TableRegions.RowHeader) {
        return shouldShowCheckbox(itemData.selectionMode) ? 'checkbox' : 'radioButton';
      }
      return 'cell';
    })();
    const extendRowSelection = selectionStart => {
      if (readOnly || rowIndex >= rowCount || region !== TableRegions.RowHeader && itemData.selectableOnlyBySelectionControl) {
        return;
      }
      const selectedRows = props.preference?.selectedRows;
      const isCheckbox = region === TableRegions.RowHeader;
      const shouldClearPrevious = (() => {
        if (itemData.selectionMode === 'toggle') return 'selectionRange';
        return event.metaKey || event.ctrlKey ? false : true;
      })();
      interactiveActionRef.current = true;
      props.setSelectRow?.({
        index: rowIndex,
        selected: (selectionMode !== 'single' || props.singleSelectionOverrides?.allowDeselection !== false) && selectedRows?.has(getRowIdentifier(rowIndex)) ? false : true,
        selectionType: isCheckbox ? 'checkbox' : 'cell',
        clearPrevious: shouldClearPrevious,
        rowCount: props.rowCount,
        rangeSelection: true,
        rowKey: getRowIdentifier,
        getRowIndex: props.getRowIndex,
        hideSelectAll: props.hideSelectAll,
        columnHeadersVisible: props.columnHeadersVisible,
        areRowsVisible: props.areRowsVisible,
        isRowDisabled,
        isRowReadOnly,
        disabledRows: props.disabledRows,
        selectionStart
      });
    };
    const handleKeyboardSelection = () => {
      const region = getRegionForCellPosition();
      const rowKey = rowIndex === -1 ? undefined : getRowIdentifier(rowIndex);
      if (readOnly || itemData.selectionMode === 'none' || rowIndex >= rowCount || region !== TableRegions.RowHeader && region !== TableRegions.ColumnRowHeader && itemData.selectableOnlyBySelectionControl || rowKey && !!isRowDisabled?.(rowKey) || rowKey && !!isRowReadOnly?.(rowKey)) {
        return;
      }
      if (itemData.hideSelectAll && region === TableRegions.ColumnRowHeader) {
        return;
      }
      const singleSelection = itemData.selectionMode === 'single' || itemData.selectionMode === 'singleRadio';
      if (!singleSelection && !itemData.hideSelectAll && region === TableRegions.ColumnRowHeader) {
        // trigger 'select all' action
        const setSelectAllRows = itemData.setSelectAllRows;
        setSelectAllRows?.({
          selectAll: !(itemData.preference?.selectAll === true),
          interactive: true
        });
      } else if (region !== TableRegions.ColumnHeader) {
        // trigger 'select row' action
        const {
          getRowIdentifier
        } = itemData;
        const isCheckbox = region === TableRegions.RowHeader && shouldShowCheckbox(itemData.selectionMode);
        const shouldClearPrevious = (() => {
          if (itemData.selectionMode === 'toggle') return false;
          return !isCheckbox && (!singleSelection && (event.metaKey || event.ctrlKey) ? false : true);
        })();
        const selected = getSelected(itemData, rowIndex);
        const rangeSelection = singleSelection ? false : event.shiftKey;
        props.setSelectRow && props.setSelectRow({
          index: rowIndex,
          selected,
          selectionType,
          clearPrevious: shouldClearPrevious,
          rowCount: props.rowCount,
          rangeSelection,
          rowKey: getRowIdentifier,
          getRowIndex: props.getRowIndex,
          hideSelectAll: props.hideSelectAll,
          columnHeadersVisible: props.columnHeadersVisible,
          areRowsVisible: props.areRowsVisible,
          isRowDisabled,
          isRowReadOnly
        });
        if (!singleSelection && rangeSelection) {
          if (event.ctrlKey) needToAnnounceAddedSelectionRange.current = true;else needToAnnounceTotalSelectionRange.current = true;
          selectionEndIndex.current = rowIndex;
        } else {
          // see if this row should be selected
          // (this logic comes from the selection logic in TableState for SelectRow)
          let shouldSelect = selected;
          if (selectionType === 'cell' && shouldClearPrevious === true && itemData.preference?.selectedRows && itemData.preference?.selectedRows?.size > 1) {
            shouldSelect = true;
          }
          // announce whether row was selected or deselected
          let text = '';
          if (!event.ctrlKey) {
            if (tableState.initialFocus) {
              text = shouldSelect ? tableNarration.createSelectedNarration(tableState.initialFocus, tableState.lastFocusedCell) : tableNarration.createDeselectedNarration();
            }
          } else {
            text = shouldSelect ? tableNarration.createRowAddedSelectionNarration(rowIndex + 1) : tableNarration.createRowRemovedSelectionNarration(rowIndex + 1);
          }
          directSpeaker?.notify(text);
        }
      }
    };
    if (isContextMenuOpen) return;
    let isNavKey = false;
    const topRowIndex = !isColumnHeaderVisible ? 0 : -1;
    let visibleRowCount = visibleRowStopIndex - visibleRowStartIndex + 1;
    let fullVisibleRowStopIndex = visibleRowStopIndex;
    const preference = itemData.preference;
    const frozenColumnCount = preference?.frozenColumnCount || 0;
    const activeClassName = document.activeElement?.className;
    const rowId = getRowIdentifier(rowIndex);
    const canEdit = !itemData.disabled && !itemData.readOnly && !isRowDisabled?.(rowId) && !isRowReadOnly?.(rowId);
    switch (event.key) {
      case 'ArrowDown':
        if (region === TableRegions.ColumnHeaderButton ||
        // if arrow was pressed inside an editable control
        activeClassName && activeClassName.indexOf(cn.table) === -1 ||
        // or the arrow was pressed on last row
        !inResizeMode && rowIndex === rowCount - 1) {
          event.preventDefault();
          return; // do nothing at all
        }

        rowIndex = !inResizeMode ? rowIndex = Math.min(rowIndex + 1, rowCount - 1) : rowIndex;
        isNavKey = true;
        // create/extend range selection iff there is an anchor position, selectionMode allows, and shift key is used
        if (itemData.selectionMode !== 'none' && !singleSelection && event.shiftKey && !event.altKey) {
          const selectionStart = props.preference?.selectedRows && props.preference?.selectedRows.size > 0 ? undefined : rowIndex - 1;
          extendRowSelection(selectionStart);
        } else if (event.ctrlKey && !event.altKey) {
          rowIndex = rowCount - 1;
        }
        region = getRegionForCellPosition();
        setRowIntoView(rowIndex);
        break;
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (region === TableRegions.ColumnHeaderButton ||
        // if arrow was pressed inside an editable control
        activeClassName && activeClassName.indexOf(cn.table) === -1 ||
        // if arrow was pressed on first column
        !inResizeMode && colIndex === 0) {
          return; // do nothing at all
        }

        if (inResizeMode) {
          resizeColumn(-5, colIndex);
          event.preventDefault();
        } else {
          const displayedCell = refGrid.current.getDisplayedCell({
            row: rowIndex,
            column: colIndex
          }).column;
          if (displayedCell > 0) {
            colIndex = Math.max(displayedCell - 1, 0); // TODO 0 assumes row header always column 0 when it exists
          }
        }

        isNavKey = !inResizeMode;
        region = getRegionForCellPosition();
        break;
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (region === TableRegions.ColumnHeaderButton ||
        // if arrow was pressed inside an editable control
        activeClassName && activeClassName.indexOf(cn.table) === -1 ||
        // if arrow was pressed on last column
        !inResizeMode && colIndex === colCount - 1) {
          return; // do nothing at all
        }

        if (inResizeMode) {
          resizeColumn(5, colIndex);
          event.preventDefault();
        } else {
          const displayedCell = refGrid.current.getDisplayedCell({
            row: rowIndex,
            column: colIndex
          }).column;
          const colSpan = colSpanValue(rowIndex, displayedCell);
          const toIndex = displayedCell + colSpan;
          if (toIndex < colCount) {
            colIndex = Math.min(toIndex, colCount - 1);
          }
        }
        isNavKey = !inResizeMode;
        region = getRegionForCellPosition();
        break;
      case 'ArrowUp':
        if (
        // if arrow was pressed inside an editable control
        activeClassName && activeClassName.indexOf(cn.table) === -1 ||
        // or arrow was pressed on last row
        !inResizeMode && rowIndex === topRowIndex) {
          event.preventDefault();
          return; // do nothing at all
        }

        if (!inResizeMode && !event.ctrlKey) {
          rowIndex = Math.max(rowIndex - 1, topRowIndex); // TODO -1 assumes only 1 header row and gives it index -1
        }

        isNavKey = true;
        region = getRegionForCellPosition();
        // create/extend range selection if target row is not in header, there is an anchor position, selectionMode allows, and shift key is used
        if (rowIndex > -1 && itemData.selectionMode !== 'none' && !singleSelection && event.shiftKey && !event.altKey) {
          const selectionStart = props.preference?.selectedRows && props.preference?.selectedRows.size > 0 ? undefined : rowIndex + 1;
          extendRowSelection(selectionStart);
        } else if (event.ctrlKey && !event.altKey) {
          // if the column header is not visible
          // go to the first row of data
          if (!isColumnHeaderVisible) {
            rowIndex = topRowIndex;
            // else if we're on the first row of data or the column header,
            // go to the column header
          } else if (rowIndex <= 0) {
            rowIndex = -1;
          }
          // otherwise, go to the first row of data
          else {
            rowIndex = 0;
          }
        }
        setRowIntoView(rowIndex);
        break;
      case 'F2':
        if (event.ctrlKey || event.shiftKey || event.altKey) break;
        event.preventDefault();
        const columnToEdit = columns?.find(column => column.key === columnKey);
        const currentRef = itemData.getControlRef(columnKey, itemData.getRowIdentifier(rowIndex));
        if (canEdit && topRef.current === document.activeElement && columnToEdit?.containsInteractiveContent) {
          // F2 puts focus on the cell contents to enter edit mode
          // The focus lock will handle exiting edit mode
          if (currentRef?.current) {
            setEditState({
              isEditing: true,
              column: columnToEdit,
              colIndex,
              rowIndex
            });
            ignoreFocusIn.current = true;
            currentRef.current.focus();
            ignoreFocusIn.current = false;
          }
        } else {
          if (editState.isEditing) {
            dispatch(TableState.actions.createFocusIndicator({
              focusIndicator: 'visible'
            }));
          }
          setEditState({
            isEditing: false
          });
        }
        break;
      case 'Enter':
        {
          event.preventDefault();
          isHandlingEnterKeyPress.current = true;
          const column = columns?.find(column => column.key === columnKey);
          if (canEdit && !editState.isEditing && (props.onActivate || column?.onActivate) && !event.ctrlKey && !event.metaKey && (!column?.containsInteractiveContent || column.onActivate) || !editState.isEditing && (event.ctrlKey && !props.doubleClickOnEnter && !props.disableOnDoubleClickKeyboardActivation || !event.ctrlKey && props.doubleClickOnEnter) && onDoubleClick) {
            // directly trigger the onActivate to avoid a full render cycle
            onActivate?.(rowIndex, colIndex, columnKey, false);
            // now dispatch to update the table state
            dispatch(TableState.actions.createPressedCell({
              cell: tableState.activeCell,
              // show pressed state on the entire row unless this column is activatable on Enter
              pressRow: !column?.onActivate
            }));
          }
          if (inResizeMode) {
            const resizedKey = keyboardResizeColumn ?? '';
            const resizedIndex = tableState.activeCell?.colIndex ?? colIndex;
            const resizedWidth = getColumnWidth(resizedKey, resizedIndex);
            setKeyboardResizeColumn(undefined);
            itemData.onColumnResize && itemData.onColumnResize(resizedKey, resizedWidth || defaultSizes.columnWidth);
          } else if (region === TableRegions.ColumnHeaderButton && isColumnHeaderButtonVisible) {
            // open the column manager
            setIsManageColumnsDialogOpen(true);
            setManageColumnsFromHeaderButton(true);
            break;
          }
          const currentRef = itemData.getControlRef(columnKey, itemData.getRowIdentifier(rowIndex));
          // we want to change the focus only when Enter is pressed on the outer cell -
          // if activateOnEnter or onActivate is set on this column, don't go into edit mode on enter
          // - run the default action instead
          if (canEdit && !column?.onActivate && !event.ctrlKey && !props.doubleClickOnEnter && column?.containsInteractiveContent) {
            // we want to change the focus only when Enter is pressed on the outer cell
            if (currentRef?.current && activeClassName && activeClassName.indexOf(cn.table) > -1 && (role === 'grid' || role === 'treegrid')) {
              setEditState({
                isEditing: true,
                column,
                colIndex,
                rowIndex
              });
              ignoreFocusIn.current = true;
              currentRef.current.focus();
              ignoreFocusIn.current = false;
            }
          }
        }
        break;
      case 'Escape':
        {
          inResizeMode && resizeColumn(0, colIndex, true);
          setKeyboardResizeColumn(undefined);
          if (editState.isEditing) {
            // prevent any default behavior from happening, as we have acted upon this event
            event.preventDefault();
            // NOVACOMMONS-23469: when table is in a popover, 'escape' to exit edit will close popover but should not
            event.stopPropagation();
            dispatch(TableState.actions.createFocusIndicator({
              focusIndicator: 'visible'
            }));
          }
          setEditState({
            isEditing: false
          });
          break;
        }
      case ' ':
        // space bar
        if (!itemData.selectableOnlyBySelectionControl && region === TableRegions.Data && !props.hideSelectedStateStyles && selectionMode !== 'none') {
          dispatch(TableState.actions.createPressedCell({
            cell: tableState.activeCell
          }));
        }
        if (region === TableRegions.ColumnHeaderButton && isColumnHeaderButtonVisible) {
          event.preventDefault(); // browser would otherwise jump-scroll down the page
          // open the column manager
          setIsManageColumnsDialogOpen(true);
          setManageColumnsFromHeaderButton(true);
          break;
        }
        const column = columns?.find(column => column.key === columnKey);
        if (region === TableRegions.ColumnHeader && !props.disabled && column && column.sortable) {
          onSort(event, column.key);
          event.preventDefault();
          break;
        }
        if (column) {
          // if space bar was pressed on the outer cell, handleKeyboardSelection
          if (activeClassName && activeClassName.indexOf(cn.table) > -1) {
            event.preventDefault(); // browser would otherwise jump-scroll down the page
            isNavKey = true;
            if (!column.preventToggleSelection && allowKeyDownSelection) {
              interactiveActionRef.current = true;
              handleKeyboardSelection();
              setAllowKeyDownSelection(false);
            }
          }
        }
        break;
      case 'a':
        if (!readOnly && itemData.selectionMode !== 'none' && itemData.selectionMode !== 'toggle' && !singleSelection && event.ctrlKey) {
          event.preventDefault();
          const setSelectAllRows = itemData.setSelectAllRows;
          // trigger 'select all' action
          setSelectAllRows && setSelectAllRows({
            selectAll: true,
            interactive: true
          });
        }
        break;
      case 'h':
        if (!event.ctrlKey && !event.altKey && !event.metaKey) {
          const helpColumn = columns?.find(helpColumn => helpColumn.key === columnKey);
          if (region === TableRegions.ColumnHeader && helpColumn?.helpProps) {
            const currentRef = itemData.getHelpRef(columnKey, itemData.getRowIdentifier(rowIndex));
            const buttonElement = currentRef?.current;
            buttonElement?.dispatchEvent(new MouseEvent('click', {
              bubbles: true,
              detail: 1
            }));
          }
        }
        break;
      case 'Home':
        // if we're in an editable control, do nothing
        if (!activeClassName || activeClassName.indexOf(cn.table) === -1) {
          break;
        }
        isNavKey = true;
        if (event.ctrlKey) {
          if (rowIndex === topRowIndex && colIndex === 0) {
            event.preventDefault();
            return;
          }
          if (rowIndex === 0 && colIndex === firstDataColIndex) {
            // if we're on the first data cell, go to the first column header
            rowIndex = topRowIndex;
            colIndex = 0;
          } else if (rowIndex === topRowIndex && topRowIndex === -1) {
            // if we're on the column header, go to the first column
            colIndex = 0;
          } else if (colIndex === 0 && firstDataColIndex === 1) {
            // if we're on the checkbox row header, go to the top row
            rowIndex = topRowIndex;
          } else {
            // otherwise, we're in the data, so go to the first data cell
            rowIndex = 0;
            colIndex = firstDataColIndex;
          }
        } else {
          if (colIndex === 0) {
            event.preventDefault();
            return;
          }
          if (hasSelectionCol || selectionMode === undefined) {
            if (colIndex > frozenColumnCount + 1) {
              colIndex = frozenColumnCount + 1;
            } else if (colIndex <= 1) {
              if (props.hideSelectAll && rowIndex === -1) {
                colIndex = 1;
              } else {
                colIndex = 0;
              }
            } else {
              colIndex = 1;
            }
          } else {
            if (colIndex > frozenColumnCount) {
              colIndex = frozenColumnCount;
            } else {
              colIndex = 0;
            }
          }
        }
        region = getRegionForCellPosition();
        setRowIntoView(rowIndex);
        break;
      case 'End':
        // if we're in an editable control, do nothing
        if (!activeClassName || activeClassName.indexOf(cn.table) === -1) {
          break;
        }
        isNavKey = true;
        if (event.ctrlKey) {
          if (rowIndex === rowCount - 1 && colIndex === colCount - 1) {
            event.preventDefault();
            return;
          }
          rowIndex = rowCount - 1;
          colIndex = colCount - 1;
        } else {
          if (colIndex === colCount - 1) {
            event.preventDefault();
            return;
          }
          if (hasSelectionCol || selectionMode === undefined) {
            if (colIndex < frozenColumnCount) {
              colIndex = frozenColumnCount;
            } else {
              colIndex = colCount - 1;
            }
          } else {
            if (colIndex < frozenColumnCount - 1) {
              colIndex = frozenColumnCount - 1;
            } else {
              colIndex = colCount - 1;
            }
          }
        }
        region = getRegionForCellPosition();
        setRowIntoView(rowIndex);
        break;
      case 'Tab':
        // TAB key
        /*
        // table has 3 tab stops: column header (if shown), data, and table options button
        // TAB from header moves focus to data area (last row of data you had previously visited)
        // SHIFT+TAB from data moves focus to column header of same row
        // general tab order appears to be:
          1) column header
          2) data rows
          3) table options button
        */
        if (keyDownOnZeroState) {
          if (!event.shiftKey) {
            // if we're tabbing forward in the zero state and we're not on the last
            // focusable item in the zero state, do nothing
            // allow the natural tab order to take you through the zero state
            if (document.activeElement !== focusableZeroStateElements.current[focusableZeroStateElements.current.length - 1]) return;
          } else if (document.activeElement !== focusableZeroStateElements.current[0]) {
            // if we're tabbing backward in the zero state and we're not on the first
            // focusable item in the zero state, do nothing
            // allow the natural tab order to take you through the zero state
            return;
          }
          if (!isColumnHeaderVisible) {
            if (event.shiftKey) {
              // if the column header is not visible and we're tabbing backward
              // from the zero state, set focus to the table so the browser
              // will move focus before the table naturally
              ignoreFocusIn.current = true;
              topRef.current?.focus();
              ignoreFocusIn.current = false;
              return;
            } else {
              // if the column header is not visible and we're tabbing forward
              // from the zero state, put focus on the lower bumper so the browser
              // will move focus beyond table naturally
              ignoreFocusIn.current = true;
              bumperRef.current?.focus();
              ignoreFocusIn.current = false;
              return;
            }
          } else if (!isColumnHeaderButtonVisible) {
            if (!event.shiftKey) {
              // if the column manager button is not visible and we're tabbing forward
              // from the zero state, put focus on the lower bumper so the browser
              // will move focus beyond table naturally
              ignoreFocusIn.current = true;
              bumperRef.current?.focus();
              ignoreFocusIn.current = false;
              return;
            }
          }

          // otherwise, if we're tabbing from the zero state and we have
          // column headers, put focus back on the table (from the zero state)
          // and fall through the rest of the tab handler code
          ignoreFocusIn.current = true;
          topRef.current?.focus();
          ignoreFocusIn.current = false;
          // if we are tabbing backward from the zero state text and there
          // are no columns in the header, let the browser move focus
          // before the table natrually (don't stop on the header)
          if (columns.length === 0 && event.shiftKey) return;
        }
        const cycleForward = event.shiftKey !== true;
        if (cycleForward) {
          if (region === TableRegions.ColumnHeader || region === TableRegions.ColumnRowHeader) {
            // move focus to data region - last visited row of current column
            isNavKey = true;
            if (shouldShowZeroState) {
              event.preventDefault();
              event.stopPropagation();
              focusFirstZeroStateElement();
              return;
            } else {
              const shouldGoToRowHeader = region === TableRegions.ColumnRowHeader;
              region = shouldGoToRowHeader ? TableRegions.RowHeader : TableRegions.Data;
              // last visited data row, but current column
              rowIndex = Math.max(0, tableState.lastFocusedDataCell?.rowIndex ?? 0);
              // no need to set the colIndex since we're staying on the current column
            }
          } else if (region === TableRegions.Data || region === TableRegions.RowHeader || region === TableRegions.ZeroStateOverlay) {
            if (!isColumnHeaderButtonVisible) {
              if (shouldShowZeroState) {
                event.preventDefault();
                event.stopPropagation();
                focusFirstZeroStateElement();
                return;
              } else {
                // put focus on the lower bumper so the browser will move focus beyond table naturally.
                ignoreFocusIn.current = true;
                bumperRef.current?.focus();
                ignoreFocusIn.current = false;
                return; // TAB should move us out of table now
              }
            }
            // move focus to table options menu
            isNavKey = true;
            event.preventDefault();
            region = TableRegions.ColumnHeaderButton;
            colIndex = -1;
            rowIndex = -1;
          } else if (region === TableRegions.ColumnHeaderButton) {
            // put focus on the lower bumper so the browser will move focus beyond table naturally.
            ignoreFocusIn.current = true;
            bumperRef.current?.focus();
            ignoreFocusIn.current = false;
            return; // TAB should move us out of table now
          }
        } else {
          if (region === TableRegions.ColumnHeaderButton) {
            if (shouldShowZeroState) {
              event.preventDefault();
              event.stopPropagation();
              focusLastZeroStateElement();
              return;
            } else {
              const newColIndex = Math.max(0, tableState.lastFocusedDataCell?.colIndex ?? 0);
              const shouldGoToRowHeader = hasSelectionColumn(itemData.selectionMode, colCount, rowCount, props.hideSelectionControlColumn) && newColIndex === 0;
              // move focus to data region - last visited row/col
              region = shouldGoToRowHeader ? TableRegions.RowHeader : TableRegions.Data;
              colIndex = newColIndex;
              rowIndex = Math.max(0, tableState.lastFocusedDataCell?.rowIndex ?? 0);
            }
            isNavKey = true;
            event.preventDefault();
            event.stopPropagation();
          } else if (region === TableRegions.Data || region === TableRegions.RowHeader) {
            if (isColumnHeaderVisible) {
              // move focus to column header - header of current column location
              isNavKey = true;
              region = TableRegions.ColumnHeader;
              rowIndex = -1;
              // no need to set the colIndex since we're staying on the current column
              event.preventDefault();
              event.stopPropagation();
            }
          } else {
            return;
          }
        }
        break;
      case 'PageUp':
        if (rowIndex === topRowIndex) {
          event.preventDefault();
          return;
        }
        isNavKey = true;
        if (rowIndex > topRowIndex) {
          if (rowIndex === 0) {
            rowIndex = topRowIndex;
          } else if (rowIndex > visibleRowStartIndex) {
            rowIndex = visibleRowStartIndex;
          } else {
            const [usedHeight, heightToFill, newFirstRowIndex] = calcHeightForPageUp(Math.max(visibleRowStartIndex - visibleRowCount, 0), visibleRowStartIndex - 1);
            // if the last row is a partial row, don't count it
            if (usedHeight > heightToFill) {
              visibleRowCount = visibleRowStartIndex - newFirstRowIndex;
              rowIndex = Math.max(newFirstRowIndex + 1, 0);
            } else {
              rowIndex = Math.max(newFirstRowIndex, 0);
            }
            refGrid.current?.scrollToItem({
              align: 'start',
              rowIndex,
              columnIndex: undefined
            });
          }
        }
        setRowIntoView(rowIndex);
        break;
      case 'PageDown':
        if (rowIndex === rowCount - 1) {
          event.preventDefault();
          return;
        }
        isNavKey = true;
        const [usedHeight, heightToFill] = calcHeightForPageDown(visibleRowStartIndex, visibleRowStopIndex);
        const currentRefGrid = refGrid.current;
        // if the last row is a partial row, go to the one before it
        if (usedHeight > heightToFill) {
          fullVisibleRowStopIndex--;
        }
        if (rowIndex < fullVisibleRowStopIndex) {
          rowIndex = Math.min(fullVisibleRowStopIndex, rowCount - 1);
        } else if (currentRefGrid?._getEstimatedTotalHeight() > heightToFill) {
          // don't try to scroll if there aren't more rows
          if (rowCount - 1 > fullVisibleRowStopIndex + 1) {
            currentRefGrid?.scrollToItem({
              align: 'start',
              rowIndex: fullVisibleRowStopIndex + 1,
              columnIndex: undefined
            });
          }
          rowIndex = Math.min(rowIndex + visibleRowCount - 1, rowCount - 1);
        }
        setRowIntoView(rowIndex);
        break;
      default:
        break;
    }
    if (!isNavKey) return;

    // if target cell is part of a colspan, then focused cell should
    // in the first column of the span range
    const displayed = refGrid.current?._getRowMetadata(rowIndex).getCell(colIndex)?.displayed;
    if (displayed > -1) {
      if (navigationKeys.includes(event.key)) navigationColumn.current = colIndex;
      colIndex = displayed;
    }

    // prevent the default key behavior for any other handlers (browser mostly)
    event.preventDefault();

    // and update the state
    // ensure the focus indicator is visible if the user has pressed any key
    updateFauxcus({
      region,
      colIndex,
      rowIndex
    });
    mounted.current && dispatch(TableState.actions.createFocusIndicator({
      focusIndicator: 'visible'
    }));

    // if appropriate, update context menu info based on new focus position
    if (region !== TableRegions.ColumnHeaderButton && region !== TableRegions.ZeroStateOverlay) {
      const newColumn = columns[colIndex];

      // determine x/y position for context menu based on row/col indices
      const isHeader = rowIndex < 0;
      const menuPosition = getContextMenuPosition(isHeader, rowIndex, colIndex);
      const newInfo = {
        column: newColumn,
        contextInfo: {
          cell: {
            column: newColumn.key,
            row: getRowIdentifier(rowIndex)
          },
          position: menuPosition
        },
        position: {
          region,
          colIndex,
          rowIndex
        }
      };
      itemData.setContextMenuInfo(newInfo);
    } else {
      itemData.setContextMenuInfo(undefined);
    }
  };
  const keyUpHandler = event => {
    if (readOnly) return;
    const isEnterKey = event.key === 'Enter';
    setAllowKeyDownSelection(true);
    dispatch(TableState.actions.createPressedCell({
      cell: undefined
    }));
    // if not currently processing an Enter key press, run the default action
    if (isHandlingEnterKeyPress.current === false) {
      defaultAction.onKeyUp(event);
    }
    if (!columns || columns.length === 0) return;
    const rowIndex = tableState.initialFocus?.rowIndex ?? 0,
      colIndex = tableState.initialFocus?.colIndex ?? 0,
      // When given a colIndex that is out of range default to the first cell
      columnKey = columns[colIndex]?.key || columns[0].key,
      rowKey = getRowIdentifier(rowIndex);
    if (isRowDisabled?.(rowKey) || isRowReadOnly?.(rowKey)) return;

    // run onDoubleClick on ctrl-Enter
    if (isEnterKey && (event.ctrlKey && !props.doubleClickOnEnter && !props.disableOnDoubleClickKeyboardActivation || !event.ctrlKey && props.doubleClickOnEnter) && onDoubleClick) {
      onDoubleClick({
        rowKey: getRowIdentifier(rowIndex),
        rowIndex,
        columnKey
      });
    } else if (!props.disabled && !event.ctrlKey && !event.altKey) {
      // for List, run onPress on the column on Enter or spacebar
      if (isEnterKey || event.key === ' ') columns[colIndex]?.onPress?.(rowIndex, columnKey);
    }
    isHandlingEnterKeyPress.current = false;
  };
  const headerHeight = isColumnHeaderVisible === false ? 0 : props.headerHeight ?? densityDefaultHeight;
  const calcHeightForPageDown = (startIndex, stopIndex) => {
    const currentRef = refGrid.current;
    const width = currentRef?.props.width;
    const height = currentRef?.props.height;
    const estimatedTotalWidth = currentRef?._getEstimatedTotalWidth();
    const scrollbarSize = estimatedTotalWidth > width ? getScrollbarSize() : 0;
    const heightToFill = height - headerHeight - (scrollbarSize + 1);
    let usedHeight = 0;
    let rowIndex = startIndex;
    while (usedHeight <= heightToFill && rowIndex <= stopIndex) {
      usedHeight += currentRef?._getRowStyle(rowIndex, 0).height ?? densityDefaultHeight;
      rowIndex++;
    }
    return [usedHeight, heightToFill];
  };
  const calcHeightForPageUp = (startIndex, stopIndex) => {
    const currentRef = refGrid.current;
    const width = currentRef?.props.width;
    const height = currentRef?.props.height;
    const estimatedTotalWidth = currentRef?._getEstimatedTotalWidth();
    const scrollbarSize = estimatedTotalWidth > width ? getScrollbarSize() : 0;
    const heightToFill = height - headerHeight - (scrollbarSize + 1);
    let usedHeight = 0;
    let rowIndex = stopIndex;
    while (usedHeight <= heightToFill && rowIndex >= startIndex) {
      usedHeight += currentRef?._getRowStyle(rowIndex, 0).height ?? densityDefaultHeight;
      rowIndex--;
    }
    return [usedHeight, heightToFill, rowIndex + 1];
  };

  // Check if a custom focus indicator is set. If so, we'll display the focus indicator
  // regardless of mouse/keyboard interaction
  const customFocusIndicator = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE);
  const focusHandler = event => {
    directSpeechTableTextRef.current = null;
    const {
      target,
      relatedTarget
    } = event;
    const keyFocusFromOutside = tableState.focusIndicator === 'unfocused' || tableState.focusIndicator === undefined;
    const initialColIndex = tableState.initialFocus?.colIndex ?? 0;
    if (ignoreFocusIn.current === true || isContextMenuOpen === true) {
      // intentional refocus within table
      return;
    }
    if (event.target === bumperRef.current && keyFocusFromOutside === false) {
      // we are focusing on the lower bumper from within the table, so allow focus to fall naturally
      return;
    }
    if (target.closest(`.${zeroStateCn.zeroState}`) !== null) {
      // focus is on the zero state - do nothing
      return;
    }
    const focusBumperFromBelow = keyFocusFromOutside && target === bumperRef.current && relatedTarget !== bumperRef.current;
    if (target !== bumperRef.current) {
      // narrate table actions when focused
      const message = tableNarration.createTableNarration();
      if (message !== null) {
        // two reasons for the delay:
        // 1) to announce after the word, 'table'
        // 2) without the delay, the next directSpeaker?.notify
        // call in this function will cancel this message
        // Also, the value of 100 is a guesstimate
        setTimeout(() => topRef.current?.contains(document.activeElement) ? directSpeaker?.notify(message) : undefined, 400);
      }

      // make sure to put a line break after each action in keyboard tooltip
      const keyboardTooltipNarration = tableNarration.createTableNarration(true);
      directSpeechTableTextRef.current = keyboardTooltipNarration || '';
    }
    if (keyFocusFromOutside && tableState.initialFocus === undefined && target !== bumperRef.current) {
      if (relatedTarget === bumperRef.current) {
        // we are focusing on table from outside and below - Shift+Tab
        if (isColumnHeaderButtonVisible) {
          updateFauxcus({
            region: TableRegions.ColumnHeaderButton,
            rowIndex: -1,
            colIndex: -1
          });
        } else if (shouldShowZeroState) {
          focusLastZeroStateElement();
          return;
        } else {
          updateFauxcus({
            region: TableRegions.Data,
            rowIndex: Math.max(0, tableState.lastFocusedDataCell?.rowIndex ?? 0),
            colIndex: tableState.lastFocusedDataCell?.colIndex ?? initialColIndex
          });
        }
      } else {
        if (isColumnHeaderVisible && getVisibleColumnKeys().length > 0) {
          const rowHeaderVisible = hasSelectionColumn(itemData.selectionMode, columns.length, props.rowCount, props.hideSelectionControlColumn);
          if (rowHeaderVisible) {
            if (props.hideSelectAll || itemData.columns.length === 0) {
              // column header, but no select-all should go to 1st column
              // or the column header of the last focused data column
              updateFauxcus({
                region: TableRegions.ColumnHeader,
                rowIndex: -1,
                colIndex: Math.max(1, tableState.lastFocusedDataCell?.colIndex ?? initialColIndex)
              });
            } else {
              // column header with select-all should go to select-all
              // or the column header of the last focused data column
              const newColIndex = tableState.lastFocusedDataCell?.colIndex ?? initialColIndex;
              const shouldGoToColumnRowHeader = newColIndex === 0;
              const region = shouldGoToColumnRowHeader ? TableRegions.ColumnRowHeader : TableRegions.ColumnHeader;
              updateFauxcus({
                region,
                rowIndex: -1,
                colIndex: newColIndex
              });
            }
          } else {
            // column header, but no row header should go to 1st column
            // or the column header of the last focused data column
            updateFauxcus({
              region: TableRegions.ColumnHeader,
              rowIndex: -1,
              colIndex: tableState.lastFocusedDataCell?.colIndex ?? initialColIndex
            });
          }
        } else {
          if (shouldShowZeroState) {
            focusFirstZeroStateElement();
          } else {
            updateFauxcus({
              region: TableRegions.Data,
              rowIndex: Math.max(0, tableState.lastFocusedDataCell?.rowIndex ?? 0),
              colIndex: tableState.lastFocusedDataCell?.colIndex ?? initialColIndex
            });
          }
        }
      }
    }
    if (event.currentTarget.contains(target)) {
      // NOTE: onMouseDown runs first, so we assume a tab focused the control if
      //     | onMouseDown did not run (and set indicator to 'hidden').
      const colIndex = tableState.initialFocus?.colIndex ?? 0;
      const column = columns?.[colIndex];
      const isHeader = tableState.initialFocus?.rowIndex === -1;
      const isInteractiveHeader = column?.headerContainsInteractiveContent;
      const isIndicatorHidden = tableState.focusIndicator === 'hidden';

      // If a custom focus indicator is set in Settings we always display the indicator
      if (mounted.current && (keyFocusFromOutside || customFocusIndicator === 'true')) {
        dispatch(TableState.actions.createFocusIndicator({
          focusIndicator: 'visible'
        }));
      }
      if (tableState.initialFocus?.region === TableRegions.ZeroStateOverlay && shouldShowZeroState) {
        focusFirstZeroStateElement();
        return;
      }
      const rowKey = getRowIdentifier(Math.max(0, tableState.lastFocusedDataCell?.rowIndex ?? 0));
      if (!isHeader && target !== topRef.current && !(column && isCellControl(event, column, props.isInteractiveControl, rowKey)) || isHeader && !isInteractiveHeader || focusBumperFromBelow) {
        // TODO - the following check for indicator is not always reliable for
        // detecting mouseclick
        // Since we've added focus lock to the mix, activateTrap calls moveFocusInside
        // on a control in the table, which gets us to this point without
        // the indicator being hidden in BaseTable's onmousedown
        //
        // If we can detect that we got here by a mouse click that didn't go through
        // Table's onMouseDown, we can not make the following call to topRef.focus
        // and fix the focus fighting
        if (!isIndicatorHidden) {
          // if we got here by a mouse click, the indicator will be hidden -
          // in that case, don't set focus because we'll do it in the mouse
          // up handler
          topRef.current?.focus();
        }
      }
      if (keyFocusFromOutside && tableState.initialFocus) {
        props.onFocusChange?.(tableState.initialFocus);
        // narration full position in table when focused from outside component
        const message = tableNarration.createNarration(tableState.initialFocus, undefined);
        if (message !== null) {
          directSpeaker?.notify(message);
        }
      }
    }
  };
  const RowContainer = (index, children, rowProps) => {
    const rowToReturn = /*#__PURE__*/_jsx("div", {
      ...rowProps,
      children: children
    });
    return props.rowContainer ? props.rowContainer(index, rowToReturn, cn.gridCell) : rowToReturn;
  };
  const DraggableRowContainer = (index, children, rowProps) => {
    const [itemPrefix, setItemPrefix] = useState();
    const refs = useRowDragAndDrop({
      rowIndex: index,
      rowKey: getRowIdentifier(index),
      prefix: dndPrefix,
      dragType: props.dragType,
      dropTypes: props.dropTypes,
      begin: () => {
        if (props.isDraggable && !props.isDraggable(index)) return;
        if (!props.dragEnabled) return;
        const rowKey = getRowIdentifier(index);
        const selectedRows = props.preference?.selectedRows;
        const selectAll = props.preference?.selectAll;
        if (selectAll !== true && props.selectionMode !== 'none' && !selectedRows?.has(rowKey)) {
          props.setSelectAllRows?.({
            selectAll: false
          });
          props.setSelectRows?.({
            selectedRows: new Set([rowKey])
          });
        }
        setDragging(true);
      },
      end: () => {
        setDragging(false);
        setItemPrefix(undefined);
        moveDropIndicator(-1);
        rowRef.current?.updateContent();
        props.onDrop?.(undefined, -1);
        setDraggingRow(-1);
      },
      drop: item => {
        if (props.isDroppable && !props.isDroppable(item, index, props.getDropLevel?.())) return;
        setDragging(false);
        setItemPrefix(undefined);
        moveDropIndicator(-1);
        const itemsToMove = props.selectionMode === 'none' ? new Set([getRowIdentifier(item?.index)]) : props.preference?.selectedRows;
        index > -1 && props.onDrop?.(item, index, props.getDropLevel?.());
        props.reorderRow?.(item.index, index, itemsToMove, props.getDropLevel?.());
        rowRef.current?.updateContent();
        setDraggingRow(-1);
      },
      hover: (item, monitor) => {
        const rect = topRef.current?.getBoundingClientRect();
        setItemPrefix(item.dndPrefix);
        if (props.isDroppable && !props.isDroppable(item, index, props.getDropLevel?.())) return;
        const offsetX = monitor.getDifferenceFromInitialOffset()?.x || 0;
        const gridInternalRef = refGrid.current;
        const isScrolling = gridInternalRef?._isScrolling();
        const indicator = props.getDropIndicatorForRow?.(index) || props.dropZoneIndicator;
        if (currentDropIndicator !== indicator) setCurrentDropIndicator(indicator);
        if (isScrolling) return;
        if (props.dndRef) {
          props.dndRef?.current?.displayDropIndicator(index);
        } else {
          const clientOffset = monitor.getClientOffset();
          const cellStyle = gridInternalRef?._getRowStyle(index, 0);
          const hoverPostion = (clientOffset?.y ?? 0) - (rect?.top ?? 0);
          const rowTopDropZone = getRowHeight(0) / 2;
          let position = 0;
          if (hoverPostion < rowTopDropZone) {
            position = 0;
          } else {
            const scrollTop = gridInternalRef?.getScrollTop() || 0;
            const offset = indicator === 'zone' ? 0 : 5;
            position = cellStyle?.top - scrollTop - offset;
            if (props.columnHeadersVisible !== false) {
              const headerHeight = props.headerHeight ?? densityDefaultHeight;
              position += headerHeight;
            }
          }
          const height = props.dropZoneIndicator === 'zone' ? cellStyle?.height : defaultSizes.dropIndicatorLineHeight;
          if (props.moveDropIndicator) {
            const width = props.expandToFillWidth && dimensions ? dimensions.width : props.width ?? defaultSizes.tableWidth;
            props.moveDropIndicator({
              rowIndex: index,
              dropIndicatorRef,
              position: position || -1,
              height,
              width,
              offsetX
            });
          } else {
            const indicator = props.getDropIndicatorForRow?.(index) ?? props.dropZoneIndicator;
            if (indicator !== 'zone') {
              moveDropIndicator(position, height);
            }
          }
        }
      },
      isDraggable: props.isDraggable
    });
    const dragEnabled = props.dragEnabled;
    const dropEnabled = props.dropEnabled;
    const ref = useRef(null);
    if (dragEnabled && dropEnabled) refs.dragRef(refs.dropRef(ref));else if (dragEnabled) refs.dragRef(ref);else if (dropEnabled) refs.dropRef(ref);
    const {
      getEmptyImage
    } = useReactDndConstructs();
    useLayoutEffect(() => {
      const preview = refs.preview;
      preview(getEmptyImage(), {
        captureDraggingState: true
      });
    });
    useEffect(() => {
      if (refs.rowIndex === index && refs.isDragging && dragging) {
        setDraggingRow(index);
        rowRef.current?.updateContent(children);
      }
    }, [children, index, refs.isDragging, refs.isOverCurrent, refs.rowIndex]);
    const indicator = props.getDropIndicatorForRow?.(index) || props.dropZoneIndicator;
    let ignoreDrop = false;
    // we should ignore the drop indicator it's not droppable
    if (refs?.isOverCurrent === true) {
      const itemToCheck = {
        index,
        id: index,
        dndPrefix: itemPrefix
      };
      if (props.isDroppable && !props.isDroppable(itemToCheck, index, props.getDropLevel?.())) ignoreDrop = true;
    }
    const updatedRowProps = {
      ...rowProps,
      className: cx(rowProps.className, indicator === 'zone' && !ignoreDrop ? refs.isOverCurrent && cn.dropRegion : undefined),
      style: {
        ...rowProps.style
      }
    };
    const width = updatedRowProps.style.width ?? defaultSizes.tableWidth;
    const rowToReturn = /*#__PURE__*/_jsx("div", {
      ...updatedRowProps,
      ref: ref,
      style: {
        ...updatedRowProps.style,
        width: {
          width
        }
      },
      children: children
    });
    return props.rowContainer ? props.rowContainer(index, rowToReturn, cn.gridCell) : rowToReturn;
  };
  const mouseDownHandler = _event => {
    // mouse down will explicitly hide the focus inidicator as
    // long as a custom indicator is not set
    dispatch(TableState.actions.createFocusIndicator({
      focusIndicator: 'hidden'
    }));
    setKeyboardResizeColumn(undefined);
  };
  const mouseUpHandler = event => {
    dispatch(TableState.actions.createPressedCell({
      cell: undefined
    }));
    // note the following call to focus must be done in mouse up
    // because if the focus call comes before the mouse up and
    // causes the page to scroll, the mouse up will not run and
    // any dialog/popup will not open
    if (customFocusIndicator === 'true' && tableState.focusIndicator !== 'visible') {
      dispatch(TableState.actions.createFocusIndicator({
        focusIndicator: 'visible'
      }));
    }
    const colIndex = tableState.initialFocus?.colIndex ?? 0;
    const column = columns?.[colIndex];
    const isHelpPopup = document.activeElement && document.activeElement.closest(`.${helpCn.container}`) !== null;
    const isManageColumnsButton = document.activeElement && document.activeElement?.closest(`.${cn.headerActionButton}`);
    const isHeader = tableState.initialFocus?.rowIndex === -1;
    const isBodyCell = !isHeader &&
    // target is not the main table element
    event.target !== topRef.current &&
    // the table DOES contain the event target somewhere
    topRef.current?.contains(event.target);
    const rowKey = getRowIdentifier(Math.max(0, tableState.lastFocusedDataCell?.rowIndex ?? 0));
    // if the user clicked in a control inside a cell, we are in edit mode
    if (isBodyCell && column && isCellControl(event, column, props.isInteractiveControl, rowKey)) setEditState({
      isEditing: true,
      column,
      colIndex,
      rowIndex: tableState.initialFocus?.rowIndex ?? 0
    });
    const isNotInBodyCellControl = isBodyCell &&
    // NOT isCellControl conditions as follows:
    // - column must provide 'cellContent' property
    // - column must have set 'containsInteractiveContent' to true
    // - target must not be table provided cell infrastructure
    !(column && isCellControl(event, column, props.isInteractiveControl, rowKey));
    const isInANonInteractiveHeader =
    // focused cell is in header
    isHeader &&
    // header does not contain interactive content
    !column?.headerContainsInteractiveContent;
    if (
    // focused location is not in the table header
    isNotInBodyCellControl || isInANonInteractiveHeader || isHelpPopup || isManageColumnsButton) {
      // if focus is not on the table's topRef and the user clicked
      // on something that is not a control in a cell, is a non-interactive
      // header, is a helpPopup, or is the manage columns button,
      // put focus on the table's topRef
      topRef.current?.focus({
        preventScroll: true
      });
    }
  };
  const clickHandler = event => {
    if (readOnly) return;
    defaultAction.onClick(event);
  };

  // Keeping the implementation same as focus out hook.
  // This approach works well and is required in this case
  // to detect focus out when nested popovers/menus/dialogs are involved.
  const timeoutId = useRef(null);
  const onFocusOutBlur = () => {
    if (!timeoutId.current) {
      timeoutId.current = setTimeout(() => {
        if (!mounted.current) return;
        dispatch(TableState.actions.createFocusIndicator({
          focusIndicator: 'unfocused'
        }));
        if (!isManageColumnsDialogOpen && !isContextMenuOpen) dispatch(TableState.actions.createFocus({
          focusedCell: undefined
        }));
        setKeyboardResizeColumn(undefined);
        props.onFocusOut?.();
      }, 0);
    }
  };
  const onFocusOutFocus = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };
  const isColumnHeaderButtonVisible = isColumnHeaderVisible === true && props.hideManageColumnsButton !== true;
  const columnHeaderButtonInFocus = tableState.initialFocus?.region === TableRegions.ColumnHeaderButton;
  const cellClickHandler = (rowIndex, columnIndex, event) => {
    // note this is not a true click, it's mouse down
    // focus on clicked cell
    setKeyboardResizeColumn(undefined);
    const info = {
      region: rowIndex === -1 ? TableRegions.ColumnHeader : TableRegions.Data,
      rowIndex,
      colIndex: columnIndex
    };

    // Show pressed state on mouse click if the user is not clicking on interactive content in a table cell
    if (event.button === 0) {
      const hasSelectionCol = hasSelectionColumn(selectionMode, columns.length, props.rowCount, props.hideSelectionControlColumn);
      const isSelectionColumn = hasSelectionCol && columnIndex === 0;
      const target = event.target;
      const isCellContainer = target.matches(`.${cn.gridCellContentContainer}`);
      const columnKey = columns[columnIndex]?.key || '';
      const column = columns?.find(column => column.key === columnKey);

      // The pressed state will only show on click for row-level activation
      if ((!column?.containsInteractiveContent && !isSelectionColumn || isCellContainer) && props.onActivate && (!props.doubleClickOnEnter || props.selectionMode !== 'none')) {
        dispatch(TableState.actions.createPressedCell({
          cell: info,
          pressRow: true
        }));
      }
    }
    if (event.button === 2) {
      mounted.current && dispatch(TableState.actions.createActiveCell({
        cell: info
      }));
    } else if (props.rowCount > rowIndex) {
      updateFauxcus(info);
    }
  };

  // if expandToFillWidth is not in effect, set the width on the grid
  const gridWidth = props.expandToFillWidth && dimensions ? undefined : props.width ?? defaultSizes.tableWidth;
  if (startMeasurementRowIndex !== undefined && startMeasurementRowIndex > -1) refGrid.current?.resetAfterRowIndex(0, false);
  if (startMeasurementColumnIndex !== undefined && startMeasurementColumnIndex > -1) refGrid.current?.resetAfterColumnIndex(0, false);
  const focusCellRef = useRef(null);
  const directSpeechTableTextRef = useRef(null);
  const setKeyboardTooltipRef = useKeyboardTooltip({
    tooltip: directSpeechTableTextRef.current ?? undefined,
    startElementRef: focusCellRef,
    traverseChildren: true
  });
  const [manageColumnsFromHeaderButton, setManageColumnsFromHeaderButton] = useState(false);
  const showSeparator = props.showSeparator;
  const tableHeaderOverrideTheme = useTableOverrideTheme(props.ignoreTableOverrideTheme, true);
  const tableBodyOverrideTheme = useTableOverrideTheme(props.ignoreTableOverrideTheme);
  const bufferRow = props.dropEnabled && props.rowCount > 0 ? 1 : 0;
  const headerButtonProps = !isColumnHeaderButtonVisible ? undefined : {
    testId: postfixTestId(props.testId, 'manage-columns-button'),
    icon: TableActions,
    onPress: () => {
      setIsManageColumnsDialogOpen(true);
      setManageColumnsFromHeaderButton(true);
    },
    disabled: props.disabled,
    role: 'img',
    tabIndex: -1,
    ariaHidden: true,
    tooltip: localize('manageColumns.button.tooltip.label'),
    ariaLabel: localize('manageColumns.button.aria')
  };
  const headerButtonHeight = headerHeight - 1;
  const headerButtonTop = props.outerBorderVisible ? '1px' : '0px';
  const headerButtonRight = props.outerBorderVisible ? '1px' : '0px';

  // add focusCellRef when header button is in faux focus
  const headerButtonHasFocus = tableState.initialFocus?.region === TableRegions.ColumnHeaderButton && tableState.focusIndicator === 'visible';
  const rect = topRef?.current?.getBoundingClientRect();
  // header height + padding
  const getVariableSizeGrid = () => {
    return /*#__PURE__*/_jsx("div", {
      ref: setDimensions,
      onDrag: e => {
        // outside table bounds
        const topHeight = (rect?.top ?? 0) + headerHeight;
        if (e.clientY < topHeight) {
          props.moveDropIndicator?.({
            rowIndex: -1,
            dropIndicatorRef
          });
          moveDropIndicator(-1);
        }
      },
      children: /*#__PURE__*/_jsx(VariableSizeGrid, {
        testId: props.testId,
        tableHeaderOverrideTheme: tableHeaderOverrideTheme,
        tableBodyOverrideTheme: tableBodyOverrideTheme,
        columnSpan: colSpanValue,
        renderRow: props.renderRow,
        addRowAttributes: props.addRowAttributes,
        addCellAttributes: props.addCellAttributes,
        onScroll: event => {
          if (dropIndicatorRef.current) dropIndicatorRef.current.style.display = 'none';
          controlsRefs.clear();
          props.onScroll && props.onScroll({
            scrollLeft: event.scrollLeft,
            scrollTop: event.scrollTop,
            verticalScrollDirection: event.verticalScrollDirection,
            horizontalScrollDirection: event.horizontalScrollDirection
          });
          setContextMenuOpen(false);
          parentScrollRowIntoView();
        },
        getRowContainer: props.dragEnabled || props.dropEnabled ? DraggableRowContainer : RowContainer,
        itemData: itemData,
        cellBackground: params => {
          const columnKey = columns[params.columnIndex] && columns[params.columnIndex].key;
          return props.cellBackground ? props.cellBackground({
            ...params,
            columnKey
          }) : undefined;
        },
        showSeparator: typeof showSeparator === 'function' ? index => showSeparator?.(index) : showSeparator,
        isRowDisabled: props.isRowDisabled || props.disabledRows ? row => {
          const key = typeof row === 'number' ? getRowIdentifier(row) : row;
          return !!isRowDisabled?.(key);
        } : undefined,
        isRowReadOnly: props.isRowReadOnly || props.readonlyRows ? row => {
          const key = typeof row === 'number' ? getRowIdentifier(row) : row;
          return !!isRowReadOnly?.(key);
        } : undefined,
        rowRef: rowIndex => {
          return elem => {
            const propsRef = props.rowRef?.(rowIndex);
            setRef(propsRef, elem);
            const internalRef = createRowRef(rowIndex);
            setRef(internalRef, elem);
          };
        },
        headerRef: headerRef,
        autoFillLastColumn: props.autoFillLastColumn,
        focusCellRef: tableState.focusIndicator === 'visible' ? focusCellRef : null,
        hideFillRows: props.hideFillRows,
        removeFillRows: props.removeFillRows,
        headerVisible: isColumnHeaderVisible,
        outerBorderVisible: props.outerBorderVisible || emptyDropZone,
        headerButtonVisible: !!headerButtonProps,
        ref: refGrid,
        focusInfo: tableState.initialFocus,
        onCellClick: cellClickHandler,
        frozenColumnCount: (props.preference?.frozenColumnCount ?? 0) + (hasSelectionColumn(itemData.selectionMode, columns.length, props.rowCount, props.hideSelectionControlColumn) ? 1 : 0),
        height: props.height ?? defaultSizes.tableHeight,
        rowCount: props.columns?.length === 0 ? 0 : props.rowCount + bufferRow,
        density: density,
        rowHeight: getRowHeight,
        headerHeight: props.headerHeight ?? densityDefaultHeight,
        width: props.expandToFillWidth && dimensions ? dimensions.width : props.width ?? defaultSizes.tableWidth,
        direction: isRTL ? 'rtl' : 'ltr',
        columnCount: columns.length + (fillerWidth > 0 ? 1 : 0),
        isLastColumnEmpty: fillerWidth > 0 ? true : false,
        columnWidth: columnIndex => {
          const columnKey = columns[columnIndex] && columns[columnIndex].key;
          return getColumnWidth(columnKey, columnIndex) || defaultSizes.columnWidth;
        },
        dataKey: props.dataKey,
        onItemsRendered:
        // NOTE: VariableSizeGrid does less work during the commit phase if this prop is not set
        !props.loadData ? event => {
          setVisibleRowStartIndex(Math.max(event.visibleRowStartIndex, 0));
          setVisibleRowStopIndex(Math.min(event.visibleRowStopIndex, props.rowCount));
        } : event => {
          const loadData = props.loadData;
          const whenLoaded = loadData({
            columnStartIndex: Math.max(event.visibleColumnStartIndex - (props.threshold ?? 0), 0),
            columnEndIndex: Math.min(event.visibleColumnStopIndex + (props.threshold ?? 0), columns.length - 1),
            rowStartIndex: Math.max(event.visibleRowStartIndex - (props.threshold ?? 0), 0),
            rowEndIndex: Math.min(event.visibleRowStopIndex + (props.threshold ?? 0), props.rowCount - 1)
          });
          setVisibleRowStartIndex(Math.max(event.visibleRowStartIndex, 0));
          setVisibleRowStopIndex(Math.min(event.visibleRowStopIndex, props.rowCount));
          const gridInternalRef = refGrid.current;
          const isScrolling = gridInternalRef?._isScrolling();
          if (isPromiseLike(whenLoaded)) {
            Promise.resolve(whenLoaded).then(() => {
              const current = refGrid.current;
              current && current.resetAfterIndices({
                columnIndex: event.visibleColumnStartIndex,
                rowIndex: event.visibleRowStartIndex,
                shouldForceUpdate: isScrolling ? false : true
              });
            });
          }
        },
        children: TableCell
      })
    });
  };
  const dropIndicatorRef = useRef(null);
  const getDropIndicator = () => {
    const indicator = currentDropIndicator || props.dropZoneIndicator;
    if (indicator === 'zone') return /*#__PURE__*/_jsx(Fragment, {});
    const width = props.expandToFillWidth && dimensions ? dimensions.width : props.width ?? defaultSizes.tableWidth;
    return /*#__PURE__*/_jsx("div", {
      ref: dropIndicatorRef,
      className: cn.dropIndicator,
      style: {
        width,
        display: 'none'
      }
    });
  };
  const gridHeight = props.removeFillRows ? props.height ?? defaultSizes.tableHeight : undefined;
  const renderContextMenuInfo = itemData.getContextMenuInfo();
  const contextMenuPosition = renderContextMenuInfo?.contextInfo.position ?? contextInfo.position;
  const contextMenuReferenceElement = renderContextMenuInfo?.contextInfo.referenceElement;
  return /*#__PURE__*/_jsxs(ContextMenu, {
    ...contextMenuItems,
    position: contextMenuPosition,
    referenceElement: contextMenuReferenceElement,
    onItemSelected: location => {
      contextMenuItems?.onItemSelected && contextMenuItems?.onItemSelected(location, {
        rowKey: contextInfo.cell?.row || '',
        columnKey: contextInfo.cell?.column || ''
      }, {
        x: contextMenuPosition?.x || 0,
        y: contextMenuPosition?.y || 0
      });
      closeContextMenu();
    },
    onDismiss: () => {
      props.cellContextMenu?.onDismiss && props.cellContextMenu?.onDismiss();
      closeContextMenu();
    },
    onContextMenu: e => {
      let contextMenuInfo = itemData.getContextMenuInfo();
      // if there is no contextMenuInfo saved, see if we can derive it from focused cell info
      if (isNullish(contextMenuInfo) && !!tableState.initialFocus) {
        const {
          rowIndex,
          colIndex
        } = tableState.initialFocus;
        const column = columns[colIndex];
        contextMenuInfo = {
          column,
          contextInfo: {
            cell: {
              column: column?.key || '',
              row: getRowIdentifier(rowIndex)
            },
            position: getContextMenuPosition(rowIndex < 0, rowIndex, colIndex)
          },
          position: {
            region: rowIndex < 0 ? TableRegions.ColumnHeader : TableRegions.Data,
            rowIndex,
            colIndex
          }
        };
        // save this new contextMenuInfo for possible future use
        itemData.setContextMenuInfo(contextMenuInfo);
      }
      if (!isNullish(contextMenuInfo)) {
        // as a MouseEvent, button is -1 when triggered by keyboard
        if (e?.button === -1) {
          // if triggering context menu from keyboard, then set the position to just below the focused cell
          const {
            rowIndex,
            colIndex
          } = contextMenuInfo.position;
          contextMenuInfo.contextInfo.position = getContextMenuPosition(rowIndex < 0, rowIndex, colIndex);
        }
        triggerContextMenu(contextMenuInfo);
      }
    },
    suppressIndicator: true,
    disabled: props.disabled || tableState.activeCell?.rowIndex !== undefined && tableState.activeCell.rowIndex > -1 && !!isRowDisabled?.(getRowIdentifier(tableState.activeCell.rowIndex)) && !!isRowReadOnly?.(getRowIdentifier(tableState.activeCell.rowIndex)),
    children: [/*#__PURE__*/_jsxs("div", {
      onFocus: e => {
        onFocusOutFocus();
        focusHandler(e);
      },
      onBlur: e => {
        if (!isCloseContextMenuRef.current || e.relatedTarget) onFocusOutBlur();
        isCloseContextMenuRef.current = false;
        // clear the narration, so if we need to repeat the last thing said, it will be noticed
        directSpeaker?.notify('');
      },
      style: {
        ...tokens,
        width: gridWidth,
        height: gridHeight,
        position: 'relative'
      },
      className: cx(cn.gridWrapper, dt.themeVariant === 'high-contrast' && cn.highContrast),
      "data-testid": postfixTestId(props.testId, 'gridWrapper'),
      children: [/*#__PURE__*/_jsxs("div", {
        "data-testid": props.testId,
        id: props.id,
        role: props.role ?? 'grid',
        "aria-label": props.ariaLabel,
        "aria-roledescription": props.ariaRoleDescription,
        "aria-labelledby": props.ariaLabelledBy,
        "aria-describedby": props.ariaDescribedBy,
        "aria-disabled": props.disabled,
        "aria-readonly": readOnly,
        "aria-colcount": columns.length,
        "aria-rowcount": itemData.rowCount,
        "aria-multiselectable": itemData.selectionMode === 'multiple' || itemData.selectionMode === 'desktop' || itemData.selectionMode === 'toggle' ? true : itemData.selectionMode === 'none' ? undefined : false,
        ref: el => {
          setRef(topRef, el);
          setKeyboardTooltipRef(el);
        },
        className: cx(cn.table, props.disabled && cn.disabled, readOnly && cn.readOnly, tableState.focusIndicator === 'visible' && cn.showFocus, !props.singleSelectionOverrides?.hideStyles && cn.selectionAccent, selectionMode === 'none' && cn.selectionModeNone, props.alternateRowShading && cn.alternateRowShading, props.selectionBorderStyle === 'dashed' && cn.selectionBorderDashed, (props.selectionBorderStyle === 'solid' || props.selectionBorderStyle === 'solid-no-bg') && cn.selectionBorderSolid, props.selectionBorderStyle === 'solid-no-bg' && cn.selectionBorderSolidNoBg,
        // use for whole row styles
        props.onActivate && !props.doubleClickOnEnter && cn.activate, props.onDoubleClick && cn.doubleClick, props.hideStateStyles && cn.hideStateStyles, props.hideSelectedStateStyles && cn.hideSelectedStateStyles, (props.hideHoverStateStyles || props.selectableOnlyBySelectionControl) && cn.hideHoverStateStyles, props.selectableOnlyBySelectionControl && cn.selectableOnlyBySelectionControl, props.hidePressedStateStyles && cn.hidePressedStateStyles, props.className),
        tabIndex: 0,
        onKeyDown: keyDownHandler,
        onKeyUp: keyUpHandler,
        onContextMenu: () => {
          setKeyboardResizeColumn(undefined);
        },
        onMouseDown: mouseDownHandler,
        onMouseUp: mouseUpHandler,
        onClick: clickHandler,
        style: {
          width: gridWidth,
          height: gridHeight,
          ...props.style
        },
        children: [props.dropEnabled && getDropIndicator(), props.dragEnabled && /*#__PURE__*/_jsx(DraggableRow, {
          numberOfRows: props.preference?.selectAll === true ? props.rowCount : props.preference?.selectedRows?.size,
          dragRowRef: rowRef,
          style: tokens
        }), getVariableSizeGrid(), shouldShowZeroState ? /*#__PURE__*/_jsx("div", {
          role: "row",
          children: /*#__PURE__*/_jsx("div", {
            ref: zeroStateRef,
            role: "gridcell",
            children: /*#__PURE__*/_jsx(ContextMenu, {
              onContextMenu: e => {
                if (!e || itemData.hasContextMenu() !== true) return;
                const info = {
                  contextInfo: {
                    position: {
                      x: e.pageX,
                      y: e.pageY
                    },
                    cell: {
                      row: '',
                      column: ''
                    }
                  },
                  position: {
                    region: TableRegions.ZeroStateOverlay,
                    rowIndex: -1,
                    colIndex: -1
                  },
                  column: undefined
                };
                itemData.updateContextMenuItems(itemData.cellContextMenu, {
                  rowKey: info.contextInfo.cell.row,
                  columnKey: info.contextInfo.cell.column
                });
                itemData.setContextMenuInfo(info);
                itemData.setContextMenuOpen(true);
                itemData.storePreviousFocusIndicator(itemData.focusIndicator);
              },
              children: /*#__PURE__*/_jsx(TableZeroState, {
                testId: postfixTestId(props.testId, 'zero-state'),
                height: props.height ?? defaultSizes.tableHeight,
                columnHeadersVisible: isColumnHeaderVisible,
                width: props.expandToFillWidth && dimensions ? dimensions.width : props.width ?? defaultSizes.tableWidth,
                headerHeight: headerHeight,
                customProps: props.emptyDataProps,
                customNoColumnProps: props.emptyColumnProps
                // if there are no rows or no columns, show "no data zero state"
                // if all columns are hidden, show "hidden columns zero state"
                ,
                variant: props.rowCount === 0 || hasNoColumns ? 'noData' : 'hiddenColumns',
                onManageColumns: () => {
                  setManageColumnsFromHeaderButton(false);
                  setIsManageColumnsDialogOpen(true);
                },
                disabled: props.disabled
              })
            })
          })
        }) : null]
      }), headerButtonProps && /*#__PURE__*/_jsx("div", {
        ref: headerButtonHasFocus && focusCellRef ? focusCellRef : undefined,
        "data-testid": postfixTestId(props.testId, 'manage-columns-button-wrapper'),
        className: cx(cn.headerActionButton, columnHeaderButtonInFocus ? cn.fauxcus : null, tableState.focusIndicator === 'visible' && cn.showFocus),
        style: {
          height: headerButtonHeight,
          insetBlockStart: headerButtonTop,
          insetInlineEnd: headerButtonRight
        },
        children: /*#__PURE__*/_jsx(Icon, {
          ...headerButtonProps,
          style: {
            height: 'auto'
          }
        })
      }), /*#__PURE__*/_jsx("div", {
        ref: bumperRef,
        tabIndex: 0
      })]
    }), /*#__PURE__*/_jsx(ManageColumnsDialog, {
      testId: postfixTestId(props.testId, 'manage-columns'),
      isOpen: isManageColumnsDialogOpen,
      columns: managableColumns(),
      orderedColumnKeys: managableColumnKeys(),
      onConfirm: orderedColumnKeys => {
        const omittedKeys = props.unmanagedColumnKeys;
        let orderedKeys = orderedColumnKeys;
        if (omittedKeys) {
          orderedKeys = omittedKeys.reduce((acc, key) => {
            const index = displayedColumnKeys?.findIndex(colKey => colKey === key);
            if (index !== -1) {
              return [...acc.slice(0, index), key, ...acc.slice(index)];
            }
            // If key is not found in displayedColumnKeys, just add it to the end
            return [...acc, key];
          }, orderedKeys);
        }
        const prevOrderedColumnKeys = props.preference?.orderedColumnKeys ?? [];
        for (const [index, prevOrderedColumnKey] of prevOrderedColumnKeys.entries()) {
          // If previously frozen column is hidden, unfreeze it
          if (index < (props.preference?.frozenColumnCount ?? 0) && !orderedKeys.includes(prevOrderedColumnKey)) {
            props.setFreezeColumn?.({
              columnKey: prevOrderedColumnKey,
              unfreeze: true
            });
          }
        }
        if (orderedKeys.length === 0) setClearAll(true);
        interactiveActionRef.current = true;
        props.setOrderedColumnKeys?.({
          orderedKeys
        });
        const omittedKeysLength = omittedKeys ? omittedKeys.length : 0;
        hasColumns.current = orderedColumnKeys.length > 0 || omittedKeysLength > 0;
        setIsManageColumnsDialogOpen(false);
      },
      onClose: () => {
        setTimeout(function () {
          let needToFocusTable = false;
          // set the focus to the manage columns header button as long as the dialog
          // was opened from the manage columns header button
          if (manageColumnsFromHeaderButton) {
            updateFauxcus({
              region: TableRegions.ColumnHeaderButton,
              rowIndex: -1,
              colIndex: -1
            });
            needToFocusTable = true;
          } else {
            // if the dialog was not opened from the manage columns button, it was
            // likely opened from the zero state button, so send focus back to the table
            const hasData = hasColumns.current && props.rowCount > 0;
            if (hasData) {
              updateFauxcus({
                region: TableRegions.ColumnHeader,
                rowIndex: -1,
                colIndex: 0
              });
              needToFocusTable = true;
            }
          }
          // make sure the focus indicator is visible
          // note: I checked and this does not cause the indicator to be
          // visible if the user closes the dialog with a mouse click, so that's
          // still working as expected
          mounted.current && dispatch(TableState.actions.createFocusIndicator({
            focusIndicator: 'visible'
          }));
          // send focus to the table unless it needs to be on the zero state
          if (!props.disableAutoFocus && needToFocusTable) {
            topRef.current?.focus();
          }
          mounted.current && setManageColumnsFromHeaderButton(false);
        }, 0);
      },
      onDismiss: () => {
        setIsManageColumnsDialogOpen(false);
      },
      hideManageColumnsSearch: props.hideManageColumnsSearch,
      hideManageColumnsSelectedSearch: props.hideManageColumnsSelectedSearch
    })]
  });
};
export const BaseTable = /*#__PURE__*/memo(UnMemoizedBaseTable);
/**
 * NOTE: Exported for external use by Theme Designer's custom component overrides
 *
 * @deprecated use `getTableDesignTokens` instead
 */
export const getTableSelectedTokens = dt => {
  if (!dt.enableNovaTokens) {
    switch (dt.baseReportThemeName) {
      case 'highcontrast2017':
      case 'highcontrast2020':
        return {
          ...dt.reportTokens.stateStandard.selected,
          'accent-bg': dt.c.secondary,
          'accent-bg-selected-disabled': dt.stateSecondary.disabledSelected.bg,
          'border-highlight-bd': dt.reportTokens.stateStandard.selected.bd,
          'internal-filter': 'invert(1)'
        };
    }
  }
  switch (dt.themeVariant) {
    case 'high-contrast':
      return {
        fg: dt.tokens['color-action-text-subtle-selected'],
        bd: dt.tokens['color-action-border-subtle-selected'],
        bg: dt.enableNovaTokens ? dt.tokens['color-container-background-0'] : setAlpha(dt.c.n, 0.4),
        filter: 'invert(1) grayscale(1)',
        'accent-bg': dt.tokens['color-secondary'],
        'accent-bg-selected-disabled': dt.tokens['color-action-background-emphasis-disabled'],
        'border-highlight-bd': dt.tokens['color-neutral'],
        'internal-filter': 'none'
      };
    case 'dark':
    default:
      return {
        fg: dt.tokens['color-action-text-subtle-selected'],
        bg: dt.tokens['color-container-background-0'],
        bd: dt.tokens['color-action-border-subtle-selected'],
        filter: 'none',
        'accent-bg': dt.tokens['color-secondary'],
        'accent-bg-selected-disabled': dt.tokens['color-action-background-emphasis-disabled'],
        'border-highlight-bd': dt.tokens['color-action-border-standard-selected'],
        'internal-filter': 'none'
      };
  }
};

/**
 * NOTE: Exported for external use by Theme Designer's custom component overrides
 *
 * @deprecated use `getTableDesignTokens` instead
 */
export const getTableTokens = (dt, cellBordersVisible, emptyDropZone, ignoreTableOverrideTheme, selectionBorderStyle, outerBorderVisible, focusIndicatorWidth) => {
  const showFocusTokens = dt.enableNovaTokens ? {
    onLightBg: {
      bd: dt.tokens['color-action-background-focus'],
      bdWidth: dt.tokens['size-action-border-width-focus']
    },
    onDarkBg: {
      bd: dt.tokens['color-action-background-focus-inverse']
    }
  } : dt.stateFocus;
  const focusWidthString = focusIndicatorWidth || showFocusTokens.onLightBg.bdWidth;
  const focusWidthNumber = parseInt(focusWidthString.replace('px', ''), 10);
  const selectionBorderWidth = 2;
  const commonTokens = {
    'drop-zone-bd': dt.tokens['color-primary'],
    'drop-zone-bg': setAlpha(dt.tokens['color-primary'], 0.1),
    'frozen-column-divider-width': dt.themeVariant === 'high-contrast' ? '2px' : '1px',
    'selection-border-style': selectionBorderStyle === 'dashed' ? 'dashed' : 'solid',
    'selection-border-color': dt.themeVariant === 'high-contrast' ? showFocusTokens.onDarkBg.bd : showFocusTokens.onLightBg.bd,
    'selection-border-width': `${selectionBorderWidth}px`,
    // used to make the focus indicator easier to see when using selectionBorderStyle
    // and a selected row has a top and bottom border
    'selection-border-focus': dt.themeVariant === 'high-contrast' || focusWidthNumber >= 3 ? 'var(--focus-width)' : '3px',
    'spacing-xs': dt.tokens['space-base-xs'],
    'spacing-xxs': dt.tokens['space-base-xxs'],
    'scroll-bar-space': cellBordersVisible !== false || outerBorderVisible !== false ? `${getScrollbarSize() + 2}px` : `${getScrollbarSize() + 1}px`
  };

  // Only calculate overrides if override theme is applied and
  // if overrides are available
  const overrides = !ignoreTableOverrideTheme && Object.entries(dt.overrides.table).map(([_, v]) => v !== undefined).reduce((prev, cur) => prev ? prev : prev && cur, true) ? getTableOverrideTokens(dt) : {};
  if (!dt.enableNovaTokens) {
    const commonLegacyTokens = {
      ...commonTokens,
      'pressed-bg': dt.reportTokens.stateStandard.active.bg,
      'pressed-filter': dt.reportTokens.stateStandard.active.filter,
      'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
      'font-size-m': dt.reportTokens.font.size.m,
      'font-weight-thick': dt.reportTokens.font.weight.thick,
      'context-area-outline-color': dt.reportTokens.stateFocus.onRightClick.bd,
      'context-area-outline': `var(--table-context-area-outline-color) ${dt.reportTokens.stateFocus.onRightClick.bdStyle} ${dt.reportTokens.stateFocus.onRightClick.bdWidth}`,
      'context-area-outline-offset': `-${dt.reportTokens.stateFocus.onRightClick.bdWidth}`,
      'cell-border': cellBordersVisible !== false ? '1px solid var(--table-cell-bd)' : 'none',
      'hover-bg': dt.reportTokens.stateStandard.hover.bg,
      'selected-filter': dt.reportTokens.stateStandard.selected.filter,
      'selected-inactive-bg': dt.reportTokens.stateStandard.selectedInactive.bg,
      'selected-inactive-filter': dt.reportTokens.stateStandard.selectedInactive.filter
    };
    switch (dt.themeVariant) {
      case 'dark':
        return {
          ...commonLegacyTokens,
          fg: dt.c.white,
          bg: dt.c.p_3,
          bd: dt.c.white15,
          'alternate-row-bg': setAlpha(dt.c.white, 0.05),
          'frozen-column-divider-color': dt.c.n,
          'row-bd': dt.c.white15,
          'cell-bd': dt.c.white15,
          'header-right-bd': dt.c.white15,
          'header-bottom-bd': dt.c.white15,
          ...overrides
        };
      case 'high-contrast':
        return {
          ...commonLegacyTokens,
          fg: dt.c.white,
          bg: dt.c.black,
          bd: dt.c.n2,
          'alternate-row-bg': setAlpha(dt.c.white, 0.05),
          'frozen-column-divider-color': dt.c.n,
          'row-bd': dt.c.n2,
          'cell-bd': dt.c.n2,
          'header-right-bd': dt.c.n2,
          'header-bottom-bd': dt.c.n2,
          ...overrides
        };
      default:
        return {
          ...commonLegacyTokens,
          fg: dt.c.n_3,
          bg: dt.c.white,
          bd: dt.c.n3,
          'alternate-row-bg': setAlpha(dt.c.n5, 1),
          'frozen-column-divider-color': dt.c.n,
          'row-bd': dt.c.n3,
          'cell-bd': dt.c.n3,
          'header-right-bd': dt.c.n3,
          'header-bottom-bd': dt.c.n3,
          ...overrides
        };
    }
  }
  return {
    ...commonTokens,
    fg: dt.tokens['color-action-text-subtle-normal'],
    bg: emptyDropZone ? setAlpha(dt.tokens['color-primary'], 0.1) : dt.tokens['color-action-background-control-normal'],
    bd: emptyDropZone ? dt.tokens['color-primary'] : dt.tokens['color-container-border-4'],
    'header-right-bd': dt.tokens['color-container-border-4'],
    'header-bottom-bd': emptyDropZone ? dt.tokens['color-primary'] : dt.tokens['color-container-border-4'],
    'hover-bg': dt.tokens['color-action-background-subtle-hover'],
    'pressed-bg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-container-background-0'] : dt.tokens['color-action-background-subtle-selected'],
    'pressed-filter': dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : 'none',
    'selected-filter': dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : 'none',
    'selected-inactive-bg': dt.tokens['color-action-background-subtle-selected-inactive'],
    'selected-inactive-filter': dt.themeVariant === 'high-contrast' ? 'invert(0.85) grayscale(1)' : 'none',
    'alternate-row-bg': setAlpha(dt.tokens['color-container-background-1'], 1),
    'context-area-outline-color': dt.tokens['color-action-border-focus'],
    'context-area-outline': `var(--table-context-area-outline-color) solid ${dt.tokens['size-action-border-width-focus']}`,
    'context-area-outline-offset': `-${dt.tokens['size-action-border-width-focus']}`,
    'cell-bd': emptyDropZone ? dt.tokens['color-primary'] : dt.tokens['color-container-border-4'],
    'cell-border': cellBordersVisible !== false ? '1px solid var(--table-cell-bd)' : 'none',
    'frozen-column-divider-color': dt.tokens['color-neutral'],
    'row-bd': dt.tokens['color-container-border-4'],
    // If the .grid-cell default background is overridden to a color with opacity, the contents of the
    // non-frozen cells will bleed through the frozen cells creating an annoying ghosting
    // effect when scrolling horizontally. (NOVACOMMONS-7339)
    'disabled-bg': dt.tokens['color-action-background-control-disabled'],
    'font-size-m': dt.tokens['font-size-body-text-m'],
    'font-weight-thick': dt.tokens['font-style-weight-700'],
    ...overrides
  };
};
export const getTableDesignTokens = (dt, opts) => {
  const focusWidthString = opts?.focusIndicatorWidth || dt.tokens['size-action-border-width-focus'];
  const focusWidthNumber = parseInt(focusWidthString.replace('px', ''), 10);
  const {
    emptyDropZone,
    cellBordersVisible,
    selectionBorderStyle,
    outerBorderVisible,
    ignoreTableOverrideTheme
  } = opts ?? {};
  const selectionBorderWidth = opts?.readOnly || opts?.disabled ? dt.tokens['size-border-width-m'] : dt.tokens['size-border-width-s'];
  const selectionBorderColor = opts?.readOnly ? dt.tokens['color-neutral'] : opts?.disabled ? dt.tokens['color-action-background-emphasis-disabled'] : dt.tokens['color-primary'];
  // Only calculate overrides if override theme is applied and
  // if overrides are available
  const overrides = !ignoreTableOverrideTheme && Object.entries(dt.overrides.table).map(([_, v]) => v !== undefined).reduce((prev, cur) => prev ? prev : prev && cur, true) ? getTableOverrideTokens(dt) : {};
  let bdColor = darken(0.05, dt.tokens['color-container-border-4']);
  switch (dt.themeVariant) {
    case 'high-contrast':
      bdColor = darken(0.2, dt.tokens['color-container-border-4']);
      break;
    case 'dark':
      bdColor = lighten(0.02, dt.tokens['color-container-border-4']);
      break;
  }
  const tokens = {
    fg: dt.tokens['color-action-text-subtle-normal'],
    bg: emptyDropZone ? setAlpha(dt.tokens['color-primary'], 0.1) : dt.tokens['color-container-background-0'],
    bd: emptyDropZone ? dt.tokens['color-primary'] : dt.tokens['color-container-border-4'],
    'header-right-bd': bdColor,
    'header-bottom-bd': emptyDropZone ? dt.tokens['color-primary'] : bdColor,
    'hover-bg': dt.tokens['color-action-background-standard-hover'],
    'pressed-bg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-container-background-0'] : dt.tokens['color-action-background-subtle-selected'],
    'pressed-filter': dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : 'none',
    'pressed-active': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-border-focus-inverse'] : dt.tokens['color-action-border-focus'],
    'selected-filter': dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : 'none',
    'selected-inactive-bg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-container-background-0'] : dt.tokens['color-action-background-subtle-selected-inactive'],
    'selected-inactive-filter': dt.themeVariant === 'high-contrast' ? 'invert(0.85) grayscale(1)' : 'none',
    'focus-selected-bg-color': dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg-color)' : 'var(--focus-light-bg-color)',
    'alternate-row-bg': dt.themeVariant === 'high-contrast' ? setAlpha(dt.tokens['color-neutral'], 0.2) : setAlpha(dt.tokens['color-container-background-1'], 1),
    'context-area-outline-color': dt.tokens['color-action-border-focus'],
    'context-area-outline': `var(--table-context-area-outline-color) solid ${dt.tokens['size-action-border-width-focus']}`,
    'context-area-outline-offset': `-${dt.tokens['size-action-border-width-focus']}`,
    'cell-bd': emptyDropZone ? dt.tokens['color-primary'] : bdColor,
    'cell-border': cellBordersVisible !== false ? '1px solid var(--table-cell-bd)' : 'none',
    'frozen-column-divider-color': dt.tokens['color-neutral'],
    'row-bd': bdColor,
    // If the .grid-cell default background is overridden to a color with opacity, the contents of the
    // non-frozen cells will bleed through the frozen cells creating an annoying ghosting
    // effect when scrolling horizontally. (NOVACOMMONS-7339)
    'disabled-bg': dt.tokens['color-action-background-control-disabled'],
    'disabled-fg': dt.tokens['color-action-text-standard-disabled'],
    'font-size-m': dt.tokens['font-size-body-text-m'],
    'font-weight-thick': dt.tokens['font-style-weight-700'],
    'row-height': '40px',
    'header-height': '40px',
    'font-family': dt.tokens['font-family-primary'],
    'sort-icon-margin': dt.tokens['space-base-s'],
    'selected-row-hover-override': dt.themeVariant !== 'high-contrast' && 'none',
    'selected-row-bg': dt.themeVariant === 'high-contrast' ? 'var(--table-fg)' : 'var(--table-selected-inactive-bg)',
    'selected-row-fg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-text-subtle-normal'] : dt.tokens['color-action-text-subtle-selected'],
    'selected-focus-bg-color': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-focus'] : 'var(--focus-light-bg-color)',
    'header-column-separator-display': opts?.hideColumnBorders ? 'none' : 'block',
    'cell-horizontal-border': opts?.hideColumnBorders ? 'solid transparent' : 'solid var(--table-cell-bd)',
    'drop-zone-bd': dt.tokens['color-primary'],
    'drop-zone-bg': setAlpha(dt.tokens['color-primary'], 0.1),
    'frozen-column-divider-width': dt.themeVariant === 'high-contrast' ? '2px' : '1px',
    'selection-border-style': selectionBorderStyle === 'dashed' ? 'dashed' : 'solid',
    'selection-border-width': '2px',
    'spacing-xs': dt.tokens['space-base-xs'],
    'spacing-xxs': dt.tokens['space-base-xxs'],
    'scroll-bar-space': cellBordersVisible !== false || outerBorderVisible !== false ? `${getScrollbarSize() + 2}px` : `${getScrollbarSize() + 1}px`,
    'selection-border-color': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-border-focus-inverse'] : dt.tokens['color-action-border-focus'],
    'selection-border-focus': dt.themeVariant === 'high-contrast' || focusWidthNumber >= 3 ? 'var(--focus-width)' : '3px',
    'anim-dur': dt.tokens['duration-200'],
    'anim-tm-enter': dt.tokens['ease-in'],
    'anim-tm-exit': dt.tokens['ease-out'],
    'focus-outline': 'calc(-1px + var(--table-focus-width) * -1)',
    'selected-focus-outline': 'var(--focus-light-bg-color)',
    'focus-outline-offset': 'calc(-1px + var(--table-focus-width) * -2)'
  };
  const densityTokens = {
    'cell-horiz-padding': getDensityValue([dt.tokens['space-base-l'], dt.tokens['space-base-m'], dt.tokens['space-base-s']], opts?.density),
    'font-size': getDensityValue([dt.tokens['font-size-body-text-l'], dt.tokens['font-size-body-text-m'], dt.tokens['font-size-body-text-s']], opts?.density),
    'icon-size': getDensityValue([dt.tokens['size-icon-l'], dt.tokens['size-icon-m'], dt.tokens['size-icon-s']], opts?.density)
  };
  const selectedTokens = {
    fg: dt.themeVariant === 'high-contrast' ? dt.tokens['color-text-neutral'] : dt.tokens['color-action-text-subtle-selected'],
    bg: dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-background-control-normal'] : dt.tokens['color-action-background-subtle-selected'],
    bd: dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-focus'] : dt.tokens['color-action-border-standard-selected'],
    filter: dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : 'none',
    'accent-bg': selectionBorderColor,
    'accent-disabled-bg': dt.tokens['color-action-background-emphasis-disabled'],
    'accent-readonly-bg': dt.tokens['color-neutral'],
    'border-highlight-bd': dt.themeVariant === 'high-contrast' ? dt.tokens['color-neutral'] : dt.tokens['color-action-border-standard-selected'],
    'internal-filter': 'none',
    'inline-start-bw': selectionBorderWidth
  };
  const dndTokens = {
    'spacing-xxs': dt.tokens['space-base-xxs'],
    bd: dt.tokens['color-container-border-4'],
    'dragged-bg': dt.tokens['color-action-background-control-normal'],
    'indicator-line-bg': dt.tokens['color-primary'],
    'indicator-loop': dt.tokens['color-text-neutral-inverse'],
    'dragged-sh': dt.tokens['shadow-depth-xl']
  };
  return {
    ...renderDesignTokens({
      ...tokens,
      ...densityTokens,
      ...overrides
    }, 'table'),
    ...renderDesignTokens(selectedTokens, 'table-selected'),
    ...renderDesignTokens(dndTokens, 'table-dnd'),
    '--scrollbar-hover-track-bg': dt.tokens['color-container-background-2'],
    ...(!dt.enableNovaTokens && getTableLegacyDesignTokens(dt, opts))
  };
};
export const getTableLegacyDesignTokens = (dt, opts) => {
  const focusWidthString = opts?.focusIndicatorWidth || dt.reportTokens.stateFocus.onLightBg.bdWidth;
  const focusWidthNumber = parseInt(focusWidthString.replace('px', ''), 10);
  const overrides = !opts?.ignoreTableOverrideTheme && Object.entries(dt.overrides.table).map(([_, v]) => v !== undefined).reduce((prev, cur) => prev ? prev : prev && cur, true) ? getTableOverrideTokens(dt) : {};
  const commonLegacyTokens = {
    'pressed-bg': dt.reportTokens.stateStandard.active.bg,
    'pressed-filter': dt.reportTokens.stateStandard.active.filter,
    'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
    'font-size-m': dt.reportTokens.font.size.m,
    'font-weight-thick': dt.reportTokens.font.weight.thick,
    'context-area-outline-color': dt.reportTokens.stateFocus.onRightClick.bd,
    'context-area-outline': `var(--table-context-area-outline-color) ${dt.reportTokens.stateFocus.onRightClick.bdStyle} ${dt.reportTokens.stateFocus.onRightClick.bdWidth}`,
    'context-area-outline-offset': `-${dt.reportTokens.stateFocus.onRightClick.bdWidth}`,
    'cell-border': opts?.cellBordersVisible !== false ? '1px solid var(--table-cell-bd)' : 'none',
    'hover-bg': dt.reportTokens.stateStandard.hover.bg,
    'selected-filter': dt.reportTokens.stateStandard.selected.filter,
    'selected-inactive-bg': dt.reportTokens.stateStandard.selectedInactive.bg,
    'selected-inactive-filter': dt.reportTokens.stateStandard.selectedInactive.filter,
    'selection-border-color': dt.themeVariant === 'high-contrast' ? dt.reportTokens.stateFocus.onDarkBg.bd : dt.reportTokens.stateFocus.onLightBg.bd,
    'selection-border-focus': dt.themeVariant === 'high-contrast' || focusWidthNumber >= 3 ? 'var(--focus-width)' : '3px'
  };
  let themeBasedTokens = {};
  switch (dt.themeVariant) {
    case 'dark':
      themeBasedTokens = {
        fg: dt.c.white,
        bg: dt.c.p_3,
        bd: dt.c.white15,
        'alternate-row-bg': setAlpha(dt.c.white, 0.05),
        'frozen-column-divider-color': dt.c.n,
        'row-bd': dt.c.white15,
        'cell-bd': dt.c.white15,
        'header-right-bd': dt.c.white15,
        'header-bottom-bd': dt.c.white15
      };
      break;
    case 'high-contrast':
      themeBasedTokens = {
        fg: dt.c.white,
        bg: dt.c.black,
        bd: dt.c.n2,
        'alternate-row-bg': setAlpha(dt.c.white, 0.05),
        'frozen-column-divider-color': dt.c.n,
        'row-bd': dt.c.n2,
        'cell-bd': dt.c.n2,
        'header-right-bd': dt.c.n2,
        'header-bottom-bd': dt.c.n2
      };
      break;
    default:
      themeBasedTokens = {
        fg: dt.c.n_3,
        bg: dt.c.white,
        bd: dt.c.n3,
        'alternate-row-bg': setAlpha(dt.c.n5, 1),
        'frozen-column-divider-color': dt.c.n,
        'row-bd': dt.c.n3,
        'cell-bd': dt.c.n3,
        'header-right-bd': dt.c.n3,
        'header-bottom-bd': dt.c.n3
      };
  }
  let selectedLegacyTokens = {};
  switch (dt.baseReportThemeName) {
    case 'highcontrast2017':
    case 'highcontrast2020':
      selectedLegacyTokens = {
        ...dt.reportTokens.stateStandard.selected,
        'accent-bg': dt.c.secondary,
        'accent-bg-selected-disabled': dt.stateSecondary.disabledSelected.bg,
        'border-highlight-bd': dt.reportTokens.stateStandard.selected.bd,
        'internal-filter': 'invert(1)'
      };
  }
  let dndLegacyTokens = {};
  switch (dt.themeVariant) {
    case 'high-contrast':
      dndLegacyTokens = {
        'dragged-bg': dt.reportTokens.backgroundsDepth.standard.bg,
        bd: dt.reportTokens.backgroundsDepth.standard.bd,
        'indicator-line-bg': dt.c.n_3,
        'indicator-loop': dt.c.white,
        'dragged-sh': dt.reportTokens.backgroundsDepth.standard.sh
      };
    case 'dark':
      dndLegacyTokens = {
        'dragged-bg': dt.reportTokens.backgroundsDepth.standard.bg,
        bd: dt.reportTokens.backgroundsDepth.standard.bd,
        'indicator-line-bg': dt.c.white15,
        'indicator-loop': dt.c.white,
        'dragged-sh': dt.reportTokens.backgroundsDepth.standard.sh
      };
    default:
      dndLegacyTokens = {
        'dragged-bg': dt.reportTokens.backgroundsDepth.standard.bg,
        bd: 'transparent',
        'indicator-line-bg': setAlpha(dt.c.n1, 0.2),
        'indicator-loop': dt.c.n_3,
        'dragged-sh': dt.reportTokens.backgroundsDepth.medium.sh
      };
  }
  return {
    ...renderDesignTokens({
      ...commonLegacyTokens,
      ...themeBasedTokens,
      ...overrides
    }, 'table'),
    ...renderDesignTokens(selectedLegacyTokens, 'table-selected'),
    ...renderDesignTokens(dndLegacyTokens, 'table-dnd')
  };
};
//# sourceMappingURL=BaseTable.js.map